/** @jsxImportSource react */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'

const STORAGE_THEME = 'app_theme'
const STORAGE_LANG = 'app_lang'

export type ThemeId = 'default' | 'violet' | 'light-pure' | 'violet-inverted' | 'black'
export type LangId = 'es' | 'en'

function readTheme(): ThemeId {
  try {
    const v = localStorage.getItem(STORAGE_THEME)
    if (v === 'violet' || v === 'light-pure' || v === 'violet-inverted' || v === 'black')
      return v as ThemeId
    if (v === 'violet-deep' || v === 'violet-soft' || v === 'light-neutral') return 'violet' // migrar temas antiguos
  } catch (_) {}
  return 'default'
}

function readLang(): LangId {
  try {
    const v = localStorage.getItem(STORAGE_LANG)
    if (v === 'en') return 'en'
  } catch (_) {}
  return 'es'
}

type ThemeLanguageContextValue = {
  theme: ThemeId
  setTheme: (id: ThemeId) => void
  lang: LangId
  setLang: (id: LangId) => void
  /** Translate key. Optional second arg: { [key]: value } to replace {{key}} in the string. */
  t: (key: string, vars?: Record<string, string | number>) => string
}

const translations: Record<LangId, Record<string, string>> = {
  es: {
    'sidebar.panel': 'Panel de Vacantes',
    'sidebar.new': 'Carga de Vacante',
    'sidebar.premium': 'Premium',
    'sidebar.settings': 'Configuración',
    'sidebar.backToAdmin': 'Panel de administración',
    'sidebar.account': 'Cuenta',
    'sidebar.logout': 'Cerrar sesión',
    'sidebar.closing': 'Cerrando...',
    'privacy.trigger': 'Aviso de Privacidad',
    'privacy.modalTitle': 'Aviso de Privacidad',
    'privacy.modalSubtitle': 'Plataforma de Reclutamiento IA',
    'privacy.closeLabel': 'Cerrar aviso de privacidad',
    'privacy.section1Title': 'Responsable del Tratamiento',
    'privacy.section1Text':
      'AI Recruiter es la entidad responsable del tratamiento de los datos personales que proporcionas durante tu proceso de reclutamiento a través de esta plataforma.',
    'privacy.section2Title': 'Datos que Recopilamos',
    'privacy.section2Intro':
      'Durante el proceso de aplicación se recopilan los siguientes datos personales:',
    'privacy.section2Item1':
      'Nombre completo y correo electrónico (a través de tu cuenta de Google)',
    'privacy.section2Item2': 'Fotografía de perfil de tu cuenta de Google',
    'privacy.section2Item3': 'Currículum Vitae (CV) en formato PDF o DOCX',
    'privacy.section2Item4':
      'Información extraída de tu CV mediante Inteligencia Artificial (experiencia laboral, educación, habilidades, etc.)',
    'privacy.section2Item5': 'Video de presentación personal',
    'privacy.section2Item6': 'Respuestas a preguntas de entrevista',
    'privacy.section2Item7': 'Información de disponibilidad y agenda de Google Calendar',
    'privacy.section3Title': 'Finalidad del Tratamiento',
    'privacy.section3Intro': 'Tus datos personales serán utilizados exclusivamente para:',
    'privacy.section3Item1': 'Evaluar tu perfil profesional para la vacante a la que aplicas',
    'privacy.section3Item2':
      'Procesar y analizar tu CV mediante herramientas de Inteligencia Artificial',
    'privacy.section3Item3': 'Generar una puntuación de compatibilidad con el perfil de la vacante',
    'privacy.section3Item4': 'Facilitar la comunicación entre tú y el reclutador',
    'privacy.section3Item5': 'Agendar entrevistas a través de Google Calendar',
    'privacy.section4Title': 'Uso de Inteligencia Artificial',
    'privacy.section4Text':
      'Esta plataforma utiliza modelos de Inteligencia Artificial (Google Gemini) para analizar tu CV, generar preguntas de entrevista personalizadas y evaluar tus respuestas. La información procesada por IA se utiliza únicamente como herramienta de apoyo en el proceso de selección, y las decisiones finales de contratación siempre son tomadas por personas.',
    'privacy.section5Title': 'Transferencia de Datos',
    'privacy.section5Text':
      'Tus datos pueden ser compartidos con los servicios de terceros necesarios para el funcionamiento de la plataforma: Google (autenticación, calendar e IA), Firebase (almacenamiento seguro) y Stripe (procesamiento de pagos del reclutador). No venderemos ni compartiremos tu información personal con terceros con fines comerciales.',
    'privacy.section6Title': 'Conservación de Datos',
    'privacy.section6Text':
      'Tus datos personales se conservarán durante el tiempo necesario para completar el proceso de reclutamiento de la vacante a la que aplicaste. Puedes solicitar la eliminación de tus datos en cualquier momento contactándonos directamente.',
    'privacy.section7Title': 'Derechos ARCO',
    'privacy.section7Intro': 'Como titular de tus datos personales, tienes derecho a:',
    'privacy.section7Access': 'Acceso:',
    'privacy.section7AccessDesc': ' Conocer qué datos tenemos sobre ti',
    'privacy.section7Rectification': 'Rectificación:',
    'privacy.section7RectificationDesc': ' Corregir datos inexactos o incompletos',
    'privacy.section7Cancellation': 'Cancelación:',
    'privacy.section7CancellationDesc': ' Solicitar la eliminación de tus datos',
    'privacy.section7Opposition': 'Oposición:',
    'privacy.section7OppositionDesc': ' Oponerte al tratamiento de tus datos',
    // Un correo concreto, no "el que te haya dado la empresa": la verificación de
    // Google exige una vía de contacto identificable en la propia política, y
    // quien la lee puede no tener ninguna otra.
    'privacy.section7Contact':
      'Para ejercer estos derechos escríbenos a recruiter@insaite.io, o al correo de contacto que te haya facilitado la empresa responsable del proceso de reclutamiento.',
    'privacy.section8Title': 'Seguridad',
    'privacy.section8Text':
      'Tus datos personales son almacenados de forma segura utilizando Google Firebase, con cifrado en tránsito y en reposo. Implementamos medidas de seguridad administrativas, técnicas y físicas para proteger tu información contra acceso no autorizado, pérdida o alteración.',
    'privacy.section9Title': 'Consentimiento',
    'privacy.section9Text':
      'Al continuar con el proceso de aplicación y subir tu CV, video o responder las preguntas, aceptas los términos de este aviso de privacidad y consientes el tratamiento de tus datos personales conforme a lo aquí descrito.',
    // Sección 10 — requisito de la verificación de Google para permisos sensibles.
    // Sin la divulgación de Uso Limitado, la solicitud se rechaza.
    // ── Términos de servicio y páginas legales públicas ──────────────
    // Existen como RUTA PÚBLICA, no sólo como modal: la verificación de Google
    // exige poder abrir la política de privacidad sin iniciar sesión.
    'legal.backHome': 'Volver al inicio',
    'legal.privacyLink': 'Aviso de privacidad',
    'legal.termsLink': 'Términos de servicio',
    'legal.contact': 'Contacto',

    'terms.title': 'Términos de Servicio',
    'terms.subtitle': 'Plataforma de Reclutamiento IA',
    'terms.section1Title': 'Qué es esta plataforma',
    'terms.section1Text':
      'AI Recruiter es una plataforma de reclutamiento que ayuda a las empresas a publicar vacantes y a evaluar candidaturas con apoyo de inteligencia artificial. Las personas candidatas suben su CV, graban una presentación en vídeo, responden preguntas y agendan su entrevista.',
    'terms.section2Title': 'Quién puede usarla',
    'terms.section2Text':
      'Es necesario ser mayor de edad y disponer de una cuenta de Google o de Microsoft para iniciar sesión. Cada instancia de la plataforma la opera la empresa que realiza el proceso de selección, que es la responsable del tratamiento de los datos de sus candidaturas.',
    'terms.section3Title': 'Uso aceptable',
    'terms.section3Intro': 'Al usar la plataforma te comprometes a:',
    'terms.section3Item1': 'Proporcionar información veraz sobre tu experiencia y formación.',
    'terms.section3Item2': 'No suplantar la identidad de otra persona.',
    'terms.section3Item3':
      'No subir contenido ilegal, ofensivo ni que infrinja derechos de terceros.',
    'terms.section3Item4': 'No intentar acceder a candidaturas o datos que no sean tuyos.',
    'terms.section4Title': 'Decisiones automatizadas',
    'terms.section4Text':
      'La inteligencia artificial genera un resumen y una puntuación orientativa de cada candidatura. Esa puntuación es una ayuda para el equipo de reclutamiento, no una decisión: la valoración final la toman personas. Puedes solicitar revisión humana a la empresa responsable del proceso.',
    'terms.section5Title': 'Tus contenidos',
    'terms.section5Text':
      'Conservas la titularidad de tu CV, tu vídeo y tus respuestas. Nos concedes únicamente el permiso necesario para procesarlos y mostrarlos a la empresa a cuya vacante te postulas, durante el proceso de selección.',
    'terms.section6Title': 'Disponibilidad del servicio',
    'terms.section6Text':
      'La plataforma se ofrece tal cual, sin garantía de disponibilidad ininterrumpida. Puede haber periodos de mantenimiento o interrupciones. No nos hacemos responsables de la decisión de contratación, que corresponde íntegramente a la empresa que publica la vacante.',
    'terms.section7Title': 'Privacidad',
    'terms.section7Text':
      'El tratamiento de tus datos personales se describe en el Aviso de Privacidad, que forma parte de estos términos.',
    'terms.section8Title': 'Cambios',
    'terms.section8Text':
      'Estos términos pueden actualizarse. La fecha de la última revisión aparece al pie. El uso continuado de la plataforma tras un cambio supone su aceptación.',
    'terms.lastUpdate': 'Última actualización: Febrero 2026',

    'landing.tagline': 'Reclutamiento con inteligencia artificial, de la vacante a la entrevista.',
    'landing.whatTitle': 'Qué hace',
    'landing.whatText':
      'AI Recruiter acompaña un proceso de selección completo: la empresa publica una vacante a partir de su descripción, la plataforma genera las preguntas de entrevista, y cada persona candidata sube su CV, graba una presentación, responde y agenda su entrevista. El equipo de reclutamiento ve las candidaturas ordenadas con un resumen y una puntuación orientativa.',
    'landing.calendarTitle': 'Integración con Google Calendar',
    'landing.calendarText':
      'Si conectas tu calendario, la plataforma consulta tus franjas ocupadas para ofrecerte sólo horarios en los que tanto tú como quien entrevista estáis libres, y crea el evento de la entrevista que confirmes. No lee el contenido de tus eventos, ni tu correo, ni tus contactos, y puedes retirar el acceso cuando quieras.',
    'landing.enter': 'Entrar a la plataforma',

    'privacy.section10Title': 'Uso de los datos de tu cuenta de Google',
    'privacy.section10Intro':
      'Si conectas tu calendario, la plataforma solicita permisos de Google Calendar y los usa únicamente para lo siguiente:',
    'privacy.section10Item1':
      'Consultar tus franjas ocupadas, para ofrecerte sólo horarios en los que tú y quien entrevista estáis libres.',
    'privacy.section10Item2': 'Crear en tu calendario el evento de la entrevista que tú confirmes.',
    'privacy.section10Item3':
      'No leemos el contenido de tus eventos, ni tus correos, ni tus contactos.',
    'privacy.section10Item4':
      'No vendemos, cedemos ni compartimos estos datos con terceros, ni los usamos para publicidad ni para entrenar modelos de inteligencia artificial.',
    'privacy.section10LimitedUse':
      'El uso que esta aplicación hace de la información recibida de las API de Google se ajusta a la Política de Datos de Usuario de los Servicios de API de Google, incluidos sus requisitos de Uso Limitado.',
    'privacy.section10Revoke':
      'Puedes retirar este acceso en cualquier momento, sin pasar por nosotros, desde myaccount.google.com/permissions. Al hacerlo dejaremos de poder consultar tu disponibilidad; los eventos ya creados permanecen en tu calendario.',
    'privacy.lastUpdate': 'Última actualización: Febrero 2026',
    'settings.tab.profile': 'Perfil',
    'settings.tab.notifications': 'Notificaciones',
    'settings.tab.privacy': 'Privacidad y legal',
    'settings.tab.theme': 'Tema',
    'settings.tab.language': 'Idioma',
    'settings.tab.administration': 'Administración',
    'settings.loading': 'Cargando configuración...',
    'settings.profile.noName': 'Sin nombre',
    'settings.profile.noEmail': 'Sin email',
    'settings.notifications.soon': 'Opciones de notificaciones por email (próximamente).',
    'settings.privacy.soon': 'Gestiona tu privacidad y datos (próximamente).',
    'settings.legal.title': 'Privacidad y legal',
    'settings.legal.privacyTitle': 'Aviso de privacidad',
    'settings.legal.privacyDesc': 'Cómo tratamos tus datos personales.',
    'settings.legal.privacyCta': 'Leer aviso',
    'settings.legal.termsTitle': 'Términos de servicio',
    'settings.legal.termsDesc': 'Reglas de uso de la plataforma.',
    'settings.legal.termsCta': 'Leer términos',
    'settings.theme.title': 'Apariencia',
    'settings.theme.default': 'Por defecto (azul)',
    'settings.theme.violet': 'Violeta',
    'settings.theme.lightPure': 'Blanco puro',
    'settings.theme.violetInverted': 'Violeta invertido',
    'settings.theme.black': 'Negro puro',
    'settings.language.title': 'Idioma',
    'settings.language.es': 'Español',
    'settings.language.en': 'English',
    'title.panel': 'Panel de Vacantes',
    'title.new': 'Carga de Vacantes',
    'title.checkout': 'Premium',
    'title.settings': 'Configuración',
    'title.edit': 'Editar vacante',
    'title.workspace': 'Workspace',
    'title.candidatesList': 'Lista de candidatos',
    'title.questions': 'Preguntas de la vacante',
    'title.metrics': 'Métricas',
    'nav.backToEdit': 'Volver a editar vacante',
    'nav.backToPanel': 'Volver al panel',
    'dashboard.intro':
      'Gestiona tus vacantes. Filtra, cambia estado y accede al workspace o a las preguntas desde aquí.',
    'dashboard.pageTitle': 'Gestión de Vacantes',
    'dashboard.breadcrumb': 'Panel de administración · Gestión de vacantes',
    'dashboard.activeVacanciesBadge': '{{count}} vacantes activas',
    'dashboard.kpiTotalVacancies': 'Total vacantes',
    'dashboard.kpiActiveApplications': 'Postulaciones activas',
    'dashboard.kpiTrendMonth': '{{sign}}{{count}} este mes',
    'dashboard.kpiAvgApplicants': 'Promedio {{avg}} candidatos / vacante',
    'dashboard.kpiOpenLabel': '{{count}} abiertas',
    'dashboard.applicantsColumn': 'Postulantes',
    'dashboard.applicantsCount': '{{count}} postulantes',
    'dashboard.totalBadge': '{count} vacantes en total',
    'dashboard.paginationShowing': 'Mostrando {from} - {to} de {total} vacantes',
    'dashboard.createVacancy': '+ Nueva vacante',
    'dashboard.loading': 'Cargando vacantes...',
    'dashboard.retry': 'Reintentar',
    'dashboard.noResultsHint': 'No hay vacantes que coincidan con los filtros.',
    'dashboard.titleColumn': 'Título',
    'dashboard.filterSearch': 'Buscar por título...',
    'dashboard.statusColumn': 'Estado',
    'dashboard.filterAll': 'Todos',
    'dashboard.createdAt': 'Fecha de creación',
    'dashboard.filterAllDates': 'Sin orden',
    'dashboard.filterDateOldest': 'Más antiguos primero',
    'dashboard.filterDateNewest': 'Más recientes primero',
    'dashboard.updatedAt': 'Última actualización',
    'dashboard.actions': 'Acciones',
    'dashboard.errorUpdate': 'No se pudo actualizar',
    'dashboard.deleteSuccess': 'Vacante "{{title}}" eliminada correctamente.',
    'dashboard.errorDelete': 'No se pudo eliminar la vacante. Inténtalo de nuevo.',
    'dashboard.tooltipWorkspace': 'Ver candidatos de esta vacante',
    'dashboard.tooltipMetrics': 'Ver métricas de esta vacante',
    'dashboard.tooltipQuestions': 'Editar preguntas de la entrevista',
    'dashboard.tooltipCopyUrl': 'Copiar URL pública',
    'dashboard.tooltipEdit': 'Editar vacante',
    'dashboard.tooltipDelete': 'Eliminar vacante',
    'dashboard.tooltipFichaPreview': 'Vista previa ficha para candidatos',
    'vacancy.fichaPreview.backToPanel': 'Volver al panel',
    'vacancy.fichaPreview.langAria': 'Idioma de la ficha',
    'vacancy.fichaPreview.downloadPdf': 'Descargar PDF',
    'vacancy.fichaPreview.loading': 'Cargando vista previa…',
    'vacancy.fichaPreview.loadError': 'No se pudo cargar la vista previa.',
    'vacancy.fichaPreview.downloadError': 'No se pudo obtener el PDF.',
    'vacancy.fichaPreview.iframeTitle': 'Vista previa ficha de vacante',
    'editor.fichaUpdatedToast':
      'Ficha para candidatos actualizada. Previsualiza desde el panel de vacantes.',
    'editor.requiredDoc.title': 'Documento para el candidato',
    'editor.requiredDoc.hint':
      'Adjunto extra (certificación, plantilla) distinto de la ficha PDF publicada.',
    'editor.requiredDoc.masterToggle': 'Solicitar documento al candidato',
    'editor.requiredDoc.assetToggle': 'Proporcionar archivo para descarga',
    'editor.requiredDoc.missingFile': 'Falta subir archivo',
    'editor.requiredDoc.uploadFile': 'Subir archivo',
    'editor.requiredDoc.replaceFile': 'Reemplazar archivo',
    'editor.requiredDoc.removeFile': 'Quitar archivo',
    'editor.requiredDoc.modalTitle': 'Subir documento obligatorio',
    'editor.requiredDoc.modalHint': 'PDF o DOCX, máximo {{mb}} MB.',
    'editor.requiredDoc.chooseFile': 'Elegir archivo',
    'editor.requiredDoc.uploading': 'Subiendo…',
    'editor.requiredDoc.invalidType': 'Solo PDF o DOCX.',
    'editor.requiredDoc.tooLarge': 'El archivo supera {{mb}} MB.',
    'editor.requiredDoc.uploadUrlError': 'No se pudo preparar la subida.',
    'editor.requiredDoc.uploadError': 'Error al subir el archivo.',
    'editor.requiredDoc.saveError': 'No se pudo guardar la configuración.',
    'editor.requiredDoc.candidateUploadToggle': 'Pedir documento al candidato (entregable)',
    'editor.requiredDoc.candidateRequiredToggle': 'Obligatorio para continuar',
    'editor.requiredDoc.candidateLabel': 'Nombre del documento',
    'candidate.requiredDoc.defaultLabel': 'Documento solicitado',
    'candidate.requiredDoc.upload': 'Subir documento',
    'candidate.requiredDoc.replace': 'Reemplazar documento',
    'candidate.requiredDoc.uploading': 'Subiendo…',
    'candidate.requiredDoc.missingHint': 'Debes subir este documento para continuar.',
    'candidate.requiredDoc.invalidType': 'Formato no permitido.',
    'candidate.requiredDoc.tooLarge': 'El archivo supera {{mb}} MB.',
    'candidate.requiredDoc.uploadFailed': 'No se pudo subir el documento.',
    'candidate.requiredDoc.downloadInstructivo': 'Descargar instructivo del reclutador',
    'candidate.application.checklistRequiredDoc': 'Documento solicitado',
    'candidate.application.updateRequiredDoc': 'Actualizar documento',
    'notification.recruiter.requiredDocUploaded.title': 'Nuevo documento del candidato',
    'notification.recruiter.requiredDocUploaded.message':
      'Un candidato subió el documento solicitado. Revísalo en Entregables.',
    'notification.recruiter.requiredDocUpdated.title': 'Documento del candidato actualizado',
    'notification.recruiter.requiredDocUpdated.message':
      'El candidato reemplazó el documento solicitado.',
    'workspace.detailTab.deliverables': 'Entregables',
    'workspace.detailNoDeliverables': 'Este candidato aún no ha subido entregables.',
    'workspace.detailDeliverableFile': 'Documento del candidato',
    'workspace.downloadRequiredDoc': 'Descargar documento',
    'publicVacante.downloadFicha': 'Descargar ficha de la vacante',
    'publicVacante.gettingFicha': 'Obteniendo ficha…',
    'dashboard.tooltipFilterSearch': 'Buscar por título de puesto',
    'dashboard.linkCopied': 'Link copiado',
    'dashboard.noTitle': 'Sin título',
    'dashboard.tooltipStatus': 'Cambiar estado de la vacante',
    'dashboard.tooltipFilterStatus': 'Filtrar por estado',
    // ──── Admin ────
    'admin.redirecting': 'Redirigiendo...',
    'admin.account': 'Cuenta',
    'admin.loading': 'Cargando...',
    'admin.dashboard': 'Dashboard',
    'admin.users': 'Usuarios',
    'admin.vacancies': 'Vacantes',
    'admin.config': 'Configuración',
    'admin.audit': 'Auditoría',
    'admin.aiUsage': 'Uso IA',
    'admin.notifications': 'Notificaciones',
    'admin.markAllRead': 'Marcar todo como leído',
    'admin.noNotifications': 'No hay notificaciones',
    'admin.breadcrumb': 'Panel de Administración',
    'admin.sidebar.expand': 'Expandir menú',
    'admin.sidebar.collapse': 'Colapsar menú',
    'admin.sidebar.panelAdmin': 'Panel de administrador',
    'admin.sidebar.panelRecruiter': 'Panel de reclutador',
    'admin.logout': 'Cerrar sesión',
    'admin.dashboard.title': 'Dashboard Administrativo',
    'admin.dashboard.breadcrumbOverview': 'Resumen General',
    'admin.dashboard.cardCandidates': 'Candidatos',
    'admin.dashboard.cardVacancies': 'Vacantes',
    'admin.dashboard.cardTeam': 'Equipo',
    'admin.dashboard.cardRecruiters': 'Reclutadores',
    'admin.dashboard.cardApplications': 'Aplicaciones',
    'admin.dashboard.cardAiUsage': 'Uso de IA',
    'admin.dashboard.aiTokensDetail': 'Entrada: {{input}} · Salida: {{output}} tokens',
    'admin.dashboard.aiCostDelta': '{{pct}}% vs mes anterior',
    'admin.dashboard.kpiDeltaWeek': '{{pct}}% vs semana anterior',
    'admin.dashboard.vacancyMixTitle': 'Vacantes activas vs cerradas',
    'admin.dashboard.vacanciesActive': 'Activas',
    'admin.dashboard.vacanciesInactive': 'Cerradas / inactivas',
    'admin.dashboard.vacanciesActiveTotal': '{{active}} activas · {{total}} totales',
    'admin.dashboard.suspendedAlert':
      '¡Atención! Hay {{count}} usuarios suspendidos que requieren revisión.',
    'admin.dashboard.kpisSection': 'Indicadores clave',
    'admin.dashboard.tabsLabel': 'Secciones del dashboard',
    'admin.dashboard.tabVisual': 'Análisis Visual & Gráficas',
    'admin.dashboard.tabTrends': 'Tendencias Semanales',
    'admin.dashboard.barChartTitle': 'Volumen de postulaciones por ciclo',
    'admin.dashboard.chartsEmpty':
      'No hay datos de tendencias para mostrar gráficas en este periodo.',
    'admin.dashboard.trendsEmpty': 'No hay filas de tendencias semanales disponibles.',
    'admin.dashboard.chartsSection': 'Análisis visual',
    'admin.dashboard.chartsPlaceholder': 'Próximamente',
    'admin.dashboard.weeklyTrends': 'Tendencias semanales',
    'admin.dashboard.insightsTitle': 'AI Insights',
    'admin.dashboard.insightsLoading': 'Generando recomendaciones a partir de tus métricas…',
    'admin.dashboard.insightsRefreshAria': 'Actualizar insight',
    'admin.dashboard.insightsUpdatedAt': 'Actualizado el {{date}}',
    'admin.dashboard.insightsEmptyLine1': 'Este es tu primer insight.',
    'admin.dashboard.insightsEmptyLine2Prefix':
      'Cuando ya tengas vacantes activas o postulaciones en el periodo que analizamos, pulsa',
    'admin.dashboard.insightsEmptyLine2Suffix':
      'para obtener el resumen. Es una acción puntual; no se regenera solo al entrar.',
    'admin.dashboard.insightsError':
      'No pudimos generar el insight en este momento. El resto del dashboard sigue disponible.',
    'admin.dashboard.showAllWeeks': 'Ver todas',
    'admin.dashboard.colWeek': 'Semana',
    'admin.dashboard.colCandidates': 'Candidatos',
    'admin.dashboard.colVacancies': 'Vacantes',
    'admin.dashboard.colApplications': 'Aplicaciones',
    'notification.newApplication.title': 'Nueva postulación completada',
    'notification.newApplication.message':
      'Un candidato completó el proceso de postulación para una vacante.',
    'notification.candidate.selected.title': '¡Fuiste seleccionado!',
    'notification.candidate.selected.message':
      'La empresa te seleccionó para la vacante. Revisa el detalle de tu postulación.',
    'notification.candidate.discarded.title': 'Actualización de tu postulación',
    'notification.candidate.discarded.message':
      'Tu candidatura no continúa en este proceso. Puedes seguir postulando a otras vacantes.',
    'notification.candidate.vacancyClosed.title': 'Vacante cerrada',
    'notification.candidate.vacancyClosed.message':
      'La posición ya fue cubierta o la vacante se cerró.',
    'admin.users.title': 'Gestión de Usuarios',
    'admin.users.totalBadge': '{count} usuarios en total',
    'admin.users.export': 'Exportar CSV',
    'admin.users.inviteButton': 'Nuevo integrante',
    'admin.users.inviteModalTitle': 'Nuevo integrante',
    'admin.users.inviteEmailLabel': 'Correo electrónico',
    'admin.users.inviteEmailPlaceholder': 'nombre@empresa.com',
    'admin.users.inviteRoleLabel': 'Rol',
    'admin.users.inviteSubmit': 'Invitar',
    'admin.users.inviteSuccess': 'Invitación registrada para {email}',
    'admin.users.inviteEmailInvalid': 'Introduce un correo con formato válido',
    'admin.users.inviteErrorUserExists': 'Ese correo ya tiene una cuenta',
    'admin.users.inviteErrorInviteExists': 'Ya hay una invitación pendiente para ese correo',
    'admin.users.filterSearchPlaceholder': 'Buscar email o nombre...',
    'admin.users.filterAllLastLogin': 'Último login',
    'admin.users.lastLogin7': 'Últimos 7 días',
    'admin.users.lastLogin30': 'Últimos 30 días',
    'admin.users.lastLogin90': 'Últimos 90 días',
    'admin.users.clear': 'Limpiar',
    'admin.users.empty': 'No se encontraron usuarios',
    'admin.users.columnUser': 'Usuario',
    'admin.users.columnRoleAssigned': 'Rol asignado',
    'admin.users.superAdminBadge': 'Super Admin',
    'admin.users.paginationShowing': 'Mostrando {from} - {to} de {total} usuarios',
    'admin.users.paginationPrev': 'Anterior',
    'admin.users.paginationNext': 'Siguiente',
    'admin.users.rowsPerPage': 'Filas por página:',
    'admin.users.auditFooter':
      'Registro de auditoría activo: Todos los cambios de rol y eventos de personificación son monitoreados por seguridad.',
    'admin.users.filterEmail': 'Email',
    'admin.users.filterUid': 'UID',
    'admin.users.filterAllRoles': 'Todos los roles',
    'admin.users.roleCandidate': 'Candidato',
    'admin.users.roleRecruiter': 'Reclutador',
    'admin.users.roleAdmin': 'Admin',
    'admin.users.filterAllStatuses': 'Todos los estados',
    'admin.users.statusActive': 'Activo',
    'admin.users.statusSuspended': 'Suspendido',
    'admin.users.search': 'Buscar',
    'admin.users.columnRole': 'Rol',
    'admin.users.columnEmail': 'Email',
    'admin.users.columnName': 'Nombre',
    'admin.users.columnStatus': 'Estado',
    'admin.users.columnLastLogin': 'Último login',
    'admin.users.columnActions': 'Acciones',
    'admin.users.activate': 'Activar',
    'admin.users.suspend': 'Suspender',
    'admin.users.impersonate': 'Personificar',
    'admin.users.impersonateTitle': 'Iniciar sesión como este usuario',
    'admin.users.changeRole': 'Cambiar rol',
    'admin.users.rolePending': 'Pendiente de asignar',
    'admin.users.ownerTitle':
      'Titular de la instancia. Es el único que puede nombrar o destituir administradores. Para cambiar su rol hay que traspasar antes la titularidad.',
    'admin.users.transfer': 'Hacer titular',
    'admin.users.transferTitle': 'Traspasar la titularidad de la instancia a esta persona',
    'admin.users.transferConfirm':
      '¿Traspasar la titularidad de la instancia a {email}? Dejarás de ser el titular, aunque seguirás siendo administrador.',
    'pending.title': 'Tu cuenta está pendiente de aprobación',
    'pending.lead':
      'Ya entraste correctamente, pero todavía no tienes un rol asignado en la plataforma.',
    'pending.hint':
      'Pídele a un administrador que te asigne un rol. En cuanto lo haga, vuelve a iniciar sesión para que tome efecto.',
    'pending.signOut': 'Cerrar sesión',
    'maintenance.title': 'Estamos en mantenimiento',
    'maintenance.defaultMessage':
      'La plataforma está en mantenimiento. Vuelve a intentar más tarde.',
    'maintenance.retry': 'Reintentar',
    'maintenance.signOut': 'Cerrar sesión',
    'maintenance.adminAccess': 'Acceso administrador',
    'maintenance.adminBanner': 'Modo mantenimiento activo — acceso solo para administradores',
    'maintenance.checking': 'Verificando estado...',
    'hiringPause.bannerTitle': 'Contratación temporalmente pausada',
    'hiringPause.bannerDismiss': 'Entendido',
    'hiringPause.defaultMessage':
      'Temporalmente no se aceptan nuevas vacantes ni aplicaciones. Inténtalo más tarde.',
    'hiringPause.recruiterHint':
      'Puedes revisar candidatos existentes; no podrás crear ni activar vacantes.',
    'hiringPause.candidateHint':
      'Las postulaciones están pausadas. Tu avance en pantalla se conserva, pero no podrás enviar CV, video, respuestas ni agendar hasta que se reactive.',
    'hiringPause.calendarHint':
      'Puedes conectar tu calendario; agendar estará disponible cuando se reactive la contratación.',
    'hiringPause.blocked.uploadCv': 'Postulaciones pausadas',
    'hiringPause.blocked.uploadVideo': 'Postulaciones pausadas',
    'hiringPause.blocked.confirmReview': 'Postulaciones pausadas',
    'hiringPause.blocked.submitAnswers': 'Postulaciones pausadas',
    'hiringPause.blocked.scheduleInterview': 'Postulaciones pausadas',
    'hiringPause.blocked.createVacancy': 'Carga de vacantes pausada',
    'hiringPause.blocked.activateVacancy': 'Activación de vacantes pausada',
    'hiringPause.blocked.saveActiveVacancy': 'Guardado de vacante activa pausado',
    'hiringPause.modalTitle': 'Acción no disponible',
    'hiringPause.modalConfirm': 'Entendido',
    'hiringPause.modal.seeBanner': 'Ver aviso en la parte superior',
    'hiringPause.modal.upload_cv.title': 'No pudimos subir tu CV',
    'hiringPause.modal.upload_cv.body': 'Las postulaciones están pausadas temporalmente.',
    'hiringPause.modal.confirm_cv_review.title': 'No pudimos guardar tu revisión',
    'hiringPause.modal.confirm_cv_review.body': 'Las postulaciones están pausadas temporalmente.',
    'hiringPause.modal.upload_video.title': 'No pudimos subir tu video',
    'hiringPause.modal.upload_video.body': 'Las postulaciones están pausadas temporalmente.',
    'hiringPause.modal.submit_answers.title': 'No pudimos enviar tus respuestas',
    'hiringPause.modal.submit_answers.body': 'Las postulaciones están pausadas temporalmente.',
    'hiringPause.modal.schedule_interview.title': 'No pudimos agendar tu entrevista',
    'hiringPause.modal.schedule_interview.body': 'Las postulaciones están pausadas temporalmente.',
    'hiringPause.modal.create_vacancy.title': 'No pudimos crear la vacante',
    'hiringPause.modal.create_vacancy.body': 'La contratación está pausada en la plataforma.',
    'hiringPause.modal.activate_vacancy.title': 'No pudimos activar la vacante',
    'hiringPause.modal.activate_vacancy.body': 'La contratación está pausada en la plataforma.',
    'hiringPause.modal.save_active_vacancy.title': 'No pudimos guardar la vacante',
    'hiringPause.modal.save_active_vacancy.body': 'La contratación está pausada en la plataforma.',
    'hiringPause.emptyStateTitle': 'Postulaciones pausadas',
    'admin.users.roleLockedTitle':
      'Es administrador por la variable ADMIN_EMAILS del despliegue. Para cambiarlo, edita esa variable.',
    'admin.users.roleAdminReadOnlyTitle':
      'Administrador. Solo el titular de la instancia puede cambiar este rol.',
    'admin.users.roleReadOnlyTitle': 'Este rol no se puede cambiar desde este panel.',
    'admin.impersonation.label': 'Viendo como usuario: {{uid}}',
    'admin.impersonation.exit': 'Salir',
    'admin.config.title': 'Configuración del Sistema',
    'admin.config.tabGeneral': 'General',
    'admin.config.tabNotifications': 'Notificaciones',
    'admin.config.platformStatus': 'Estado de plataforma',
    'admin.config.operationalLimits': 'Límites operativos',
    'admin.config.maintenanceMode': 'Modo mantenimiento',
    'admin.config.maintenanceMessage': 'Mensaje de mantenimiento',
    'admin.config.hiringPause': 'Pausa global de contratación',
    'admin.config.hiringPauseMessage': 'Mensaje de pausa de contratación',
    'admin.config.confirmMaintenance':
      '¿Activar modo mantenimiento? Solo administradores podrán entrar.',
    'admin.config.confirmHiringPause':
      '¿Pausar contratación globalmente? No se podrán crear vacantes ni aplicaciones.',
    'admin.config.confirmMaintenanceTitle': 'Activar modo mantenimiento',
    'admin.config.confirmHiringPauseTitle': 'Pausar contratación global',
    'admin.config.previewSection': 'Vista previa para usuarios',
    'admin.config.previewTabCandidate': 'Vista candidato',
    'admin.config.previewTabRecruiter': 'Vista reclutador',
    'admin.config.confirmAction': 'Confirmar',
    'admin.config.limitsFree': 'Límites (plan free)',
    'admin.config.limitVacancies': 'Máximo de vacantes por reclutador (free)',
    'admin.config.generalKeyValue': 'General (clave-valor)',
    'admin.config.noKeys': 'No hay otras claves. Añade una abajo si lo necesitas.',
    'admin.config.newKeyPlaceholder': 'Nueva clave',
    'admin.config.newValuePlaceholder': 'Valor (texto o JSON)',
    'admin.config.add': 'Añadir',
    'admin.config.saving': 'Guardando...',
    'admin.config.save': 'Guardar',
    'admin.config.savedMsg': 'Configuración guardada.',
    'admin.config.notifPlatformTitle': 'Notificaciones de la plataforma',
    'admin.config.notifDescription':
      'Resumen de notificaciones (nuevas aplicaciones, avisos del sistema). La campana del header muestra el resumen; aquí puedes ver el detalle completo.',
    'admin.config.deleteAll': 'Borrar todas',
    'admin.config.deleteConfirm': '¿Borrar todas las notificaciones?',
    'admin.config.markRead': 'Marcar leída',
    'admin.config.labelVacancy': 'Vacante',
    'admin.config.labelIdVacancy': 'ID vacante',
    'admin.config.labelCandidate': 'Candidato',
    'admin.config.labelEmail': 'Email',
    'admin.config.labelIdApp': 'ID aplicación',
    'admin.config.goToPanel': 'Ir al panel',
    'admin.audit.title': 'Registro de Auditoría',
    'admin.audit.filterAdminEmail': 'Admin (email)',
    'admin.audit.filterAllActions': 'Todas las acciones',
    'admin.audit.filterFrom': 'Desde',
    'admin.audit.filterTo': 'Hasta',
    'admin.audit.search': 'Buscar',
    'admin.audit.exportCsv': 'Exportar CSV',
    'admin.audit.colDate': 'Fecha',
    'admin.audit.colAdmin': 'Admin',
    'admin.audit.colAction': 'Acción',
    'admin.audit.colTarget': 'Objetivo',
    'admin.audit.colIp': 'IP',
    'admin.audit.prev': 'Anterior',
    'admin.audit.next': 'Siguiente',
    'admin.audit.offsetLabel': 'Offset: {offset}',
    'admin.audit.totalBadge': '{count} eventos en total',
    'admin.audit.paginationShowing': 'Mostrando {from} - {to} de {total} eventos',
    'admin.audit.empty': 'No hay eventos registrados.',
    'admin.aiMonitoring.title': 'Monitoreo de IA',
    'admin.aiMonitoring.breadcrumb': 'Monitoreo de IA',
    'admin.aiMonitoring.pageTitle': 'Monitoreo de IA & Consumo de Tokens',
    'admin.aiMonitoring.callsRegistered': '{{count}} llamadas registradas',
    'admin.aiMonitoring.kpisSection': 'Indicadores de uso de IA',
    'admin.aiMonitoring.kpiTokens': 'Total tokens (30D)',
    'admin.aiMonitoring.kpiTokensBreakdown': 'Entrada {{input}} · Salida {{output}}',
    'admin.aiMonitoring.kpiCost': 'Costo acumulado',
    'admin.aiMonitoring.kpiCostHint': 'Costo computacional estimado del Copiloto ATS',
    'admin.aiMonitoring.kpiSuccess': 'Llamadas exitosas',
    'admin.aiMonitoring.kpiSuccessMeta': '{{ok}} / {{total}} ejecuciones',
    'admin.aiMonitoring.filterCaller': 'Caller o ID',
    'admin.aiMonitoring.filterModel': 'Modelo LLM',
    'admin.aiMonitoring.filterFrom': 'Desde',
    'admin.aiMonitoring.filterTo': 'Hasta',
    'admin.aiMonitoring.filterApply': 'Filtrar',
    'admin.aiMonitoring.tableSection': 'Registro de ejecuciones',
    'admin.aiMonitoring.colCost': 'Costo estimado',
    'admin.aiMonitoring.colStatus': 'Estado',
    'admin.aiMonitoring.empty': 'No hay llamadas registradas con estos filtros.',
    'admin.aiMonitoring.summary':
      'Registro de llamadas a Gemini (tokens por llamada). Esta página: {{total}} tokens.',
    'admin.aiMonitoring.colDate': 'Fecha y hora',
    'admin.aiMonitoring.colModel': 'Modelo LLM',
    'admin.aiMonitoring.colCaller': 'Servicio (caller)',
    'admin.aiMonitoring.colInput': 'Entrada',
    'admin.aiMonitoring.colOutput': 'Salida',
    'admin.aiMonitoring.colTotal': 'Total tokens',
    'admin.aiMonitoring.colError': 'Error',
    'admin.aiMonitoring.paginationShowing': 'Mostrando {from} - {to} de {total} llamadas',
    'common.errorUnknown': 'Ocurrió un error. Inténtalo de nuevo.',
    'common.confirmDelete': 'Confirmar eliminación',
    'common.errorLoad': 'No se pudo cargar. Comprueba tu conexión e inténtalo de nuevo.',
    'common.errorSave': 'No se pudo guardar. Comprueba tu conexión e inténtalo de nuevo.',
    'modal.cancel': 'Cancelar',
    'modal.cannotUndo': 'Esta acción no se puede deshacer.',
    'modal.delete': 'Eliminar',
    'modal.deleteVacancyConfirm': '¿Eliminar la vacante "{{title}}"?',
    'modal.deleteQuestionConfirm': '¿Eliminar esta pregunta?',
    'modal.savingChanges': 'Guardando cambios...',
    'modal.savedSuccess': 'Cambios guardados correctamente.',
    'modal.logoutTitle': 'Cerrar sesión',
    'modal.logoutMessage': '¿Estás seguro de que deseas cerrar sesión?',
    'modal.logoutConfirm': 'Sí, cerrar sesión',
    'modal.vacancySavedSuccess': 'Vacante guardada con éxito',
    'modal.questionsSavedSuccess': 'Preguntas guardadas con éxito',
    'modal.done': 'Listo',
    'editor.statusDraft': 'Borrador',
    'editor.statusActive': 'Activo',
    'editor.statusPaused': 'Pausado',
    'editor.statusClosed': 'Cerrado',
    'editor.statusLabel': 'Estado',
    'editor.savingDraft': 'Guardando borrador...',
    'draft.saving': 'Guardando…',
    'draft.savedAgo': 'Guardado · hace {{minutes}}min',
    'draft.offline': 'Sin conexión — guardado localmente',
    'draft.blockedPublished':
      'Esta vacante ya está activa, los cambios no se guardan como borrador',
    'draft.blockedSubmitted': 'Ya enviaste tus respuestas; no se guardan más borradores',
    'draft.discardDraft': 'Descartar borrador y volver a la última versión publicada',
    'draft.discardConfirmTitle': 'Descartar borrador',
    'draft.discardConfirmMessage':
      'Se eliminará el autoguardado y volverás a los datos guardados oficialmente. ¿Continuar?',
    'draft.discardConfirm': 'Confirmar',
    'dashboard.draftEditedAgo': 'Editada hace {{minutes}}min',
    'editor.publishing': 'Publicando vacante...',
    'editor.pausing': 'Pausando vacante...',
    'editor.closing': 'Cerrando vacante...',
    'editor.saving': 'Guardando...',
    'editor.saveChanges': 'Guardar Cambios',
    'editor.continueToQuestions': 'Continuar a preguntas',
    'editor.addHardSkill': 'Agregar técnica',
    'editor.addSoftSkill': 'Agregar blanda',
    'editor.skillsDetectedCount': '{{count}} detectadas',
    'editor.cardMainTitle': 'Datos principales de la vacante',
    'editor.cardCompensationTitle': 'Condiciones y compensación',
    'editor.successDraft': 'Borrador guardado',
    'editor.successPublished': 'Vacante publicada',
    'editor.successPaused': 'Vacante pausada',
    'editor.successClosed': 'Vacante cerrada',
    'editor.errorSaveHint': 'Error al guardar.',
    'editor.positionTitle': 'Título de la posición',
    'editor.vacancySummary': 'Resumen de la vacante',
    'editor.intro':
      'Revisa y completa los datos. Elige el estado (Borrador, Activo, Pausado o Cerrado) y guarda. Luego podrás configurar las preguntas de la entrevista.',
    'editor.hintFirstSteps':
      'Empieza por subir la descripción del puesto en "Cargar vacante" o crea una desde el panel.',
    'editor.dataLoadedBetter':
      'Hemos extraído los datos del documento. Revisa y corrige lo que necesites antes de publicar.',
    'editor.softSkills': 'Habilidades blandas',
    'editor.location': 'Ubicación',
    'editor.addLocation': 'Agregar',
    'editor.locationCitiesCount': '{{count}} ciudades agregadas',
    'editor.errorLocationMaxLength':
      'La ubicación no puede superar 120 caracteres. Quita ciudades o acorta los nombres.',
    'editor.modality': 'Modalidad',
    'editor.modalityPresencial': 'Presencial',
    'editor.modalityRemoto': 'Remoto',
    'editor.modalityHibrido': 'Híbrido',
    'editor.salaryMin': 'Salario Mínimo',
    'editor.salaryMax': 'Salario Máximo',
    'editor.currency': 'Moneda',
    'editor.salaryConfidential': 'Mostrar salario como "Confidencial" al candidato',
    'editor.experienceYears': 'Experiencia Requerida (Años)',
    'editor.hardSkills': 'Habilidades Técnicas (Duras)',
    'editor.placeholderTitle': 'Ej: Senior Full Stack Developer',
    'editor.placeholderSummary': 'Describe brevemente la vacante...',
    'editor.placeholderLocation': 'Ej: Ciudad de México, CDMX',
    'editor.placeholderSalaryMin': 'Ej: 30000',
    'editor.placeholderSalaryMax': 'Ej: 45000',
    'editor.placeholderSkills': 'Separa con saltos de línea. Ej: Python\\nSQL\\nAWS',
    'editor.noTitle': 'Sin Título',
    'editor.waitingAnalysis': 'Esperando resultados del análisis IA y conexión con el Backend.',
    'editor.copyUrl': 'Copiar URL',
    'editor.copied': '¡Copiado!',
    'editor.copyUrlDraft':
      'Publica la vacante antes de compartir el enlace: en borrador, quien lo abra verá que no está disponible.',
    'editor.errorNoId': 'Error: No se proporcionó ID de vacante',
    'editor.loadingVacancy': 'Cargando datos de la vacante...',
    'editor.loadSuccess': 'Datos cargados correctamente. Puedes editar la vacante.',
    'editor.copyUrlPrompt': 'Copia la URL:',
    'editor.placeholderSoftSkills': 'Ej: Comunicación\nLiderazgo',
    'editor.errorLoadHint': 'Error al cargar:',
    'questions.generatedCount': 'Preguntas Generadas ({{count}})',
    'questions.difficulty': 'Dificultad',
    'questions.loading': 'Cargando preguntas...',
    'questions.questionType': 'Tipo de pregunta:',
    'questions.typeLabel': 'Tipo:',
    'questions.typeTechnical': 'Técnica',
    'questions.typeBehavioral': 'Comportamiento',
    'questions.typeCulture': 'Cultura',
    'questions.difficultyEasy': 'Fácil',
    'questions.difficultyMedium': 'Medio',
    'questions.difficultyHard': 'Difícil',
    'questions.editQuestion': 'Editar esta pregunta',
    'questions.placeholderQuestion': 'Escribe la pregunta...',
    'questions.noQuestionsYet':
      'No hay preguntas generadas aún. Las preguntas se generan automáticamente cuando guardas la vacante. Si acabas de guardar, espera unos segundos y recarga la página.',
    'questions.regenerating': 'Regenerando preguntas...',
    'questions.regenerateSuccess':
      '✅ Nuevas preguntas generadas. Por favor, revísalas y aprueba si te agradan.',
    'questions.deleteQuestion': 'Eliminar esta pregunta',
    'questions.deleteError': 'Error al eliminar la pregunta. Por favor, intenta de nuevo.',
    'questions.loadError': 'Error al cargar las preguntas. Por favor, intenta de nuevo.',
    'questions.saving': 'Guardando preguntas...',
    'questions.regenerateError': 'Error al regenerar preguntas.',
    'questions.addQuestionError': 'Error al generar nueva pregunta.',
    'questions.deleteErrorShort': 'Error al eliminar pregunta.',
    'questions.savingChanges': 'Guardando cambios...',
    'questions.updateError': 'Error al actualizar pregunta.',
    'questions.deleteSuccess': 'Pregunta eliminada correctamente.',
    'questions.saveEdit': 'Guardar cambios',
    'questions.saveChanges': 'Guardar Cambios',
    'questions.saveQuestions': 'Guardar Preguntas',
    'questions.cancelEdit': 'Cancelar edición',
    'questions.confirmDelete': 'Confirmar Eliminación',
    'questions.deleteConfirmMessage': '¿Estás seguro de que deseas eliminar esta pregunta?',
    'questions.deleting': 'Eliminando...',
    'questions.regenerate': '🔄 Generar Preguntas',
    'questions.generateAnother': 'Generar otra pregunta',
    'workspace.intro':
      'Revisa los candidatos que aplicaron a esta vacante y sus scores de idoneidad.',
    'workspace.compatibilityBreakdown': 'Desglose de compatibilidad',
    'workspace.candidateAnswers': 'Respuestas del candidato',
    'workspace.noCandidates': 'Aún no hay candidatos disponibles',
    'workspace.noCandidatesDesc': 'Los candidatos aparecerán aquí cuando apliquen a tu vacante.',
    'workspace.emptyHint': 'Comparte el enlace de la vacante para recibir postulaciones.',
    'workspace.deleteSuccess': 'Postulación eliminada.',
    'workspace.deleteError': 'Error al eliminar la postulación.',
    'workspace.deleteConfirmMessage':
      '¿Eliminar la postulación de {{name}}? Esta acción no se puede deshacer.',
    'workspace.confirmDelete': 'Confirmar Eliminación',
    'workspace.tooltipDelete': 'Eliminar postulación',
    'workspace.deleteWarning':
      'Esta acción no se puede deshacer y eliminará todos los datos asociados al candidato en esta vacante.',
    'vacancy.saved': 'Vacante guardada correctamente',
    'login.welcome': 'Bienvenido',
    'login.subtitle': 'Plataforma de Reclutamiento IA',
    'login.continueGoogle': 'Continuar con Google',
    'login.connecting': 'Conectando...',
    'login.accessingPanel': 'Accediendo al Panel...',
    'login.errorServer': 'Error al procesar la respuesta del servidor.',
    'login.footer': '© AI Recruiter',
    'upload.title': 'Deja que la IA arme tu vacante',
    'upload.subtitle':
      'Arrastra la descripción del puesto. Extraemos título, requisitos y salario en segundos.',
    'upload.extractionEngineLabel': 'Motor de extracción inteligente',
    'upload.featuresTitle': 'Extracción automatizada de parámetros',
    'upload.feature1': 'Título del puesto y años de experiencia mínima.',
    'upload.feature2': 'Resumen de la vacante.',
    'upload.feature3': 'Rango salarial, moneda y si el salario es confidencial.',
    'upload.feature4': 'Habilidades técnicas y blandas, ubicación y modalidad de trabajo.',
    'upload.flowRawLabel': 'JD (PDF o Word)',
    'upload.flowOutputLabel': 'Parámetros de la vacante',
    'upload.nextStep': 'Después revisas los datos y publicas cuando quieras.',
    'upload.formatsLabel': 'Formatos aceptados:',
    'upload.clickInstruction': 'Haz clic en la esfera para seleccionar',
    'upload.dropZoneHint':
      'Suelta el archivo en la esfera de la derecha, no en las etiquetas de formato.',
    'upload.formatBadgePdf': 'Formato PDF',
    'upload.formatBadgeDocx': 'Formato Word',
    'upload.dropHere': 'Arrastra aquí tu descripción (PDF o Word).',
    'upload.dropZoneLead': 'Suelta el documento directamente en la zona interactiva',
    'upload.fileSizeLimit': 'Límite: {{mb}} MB',
    'upload.browseFiles': 'Explorar en el ordenador',
    'upload.privacyNote': 'Tus descripciones se procesan de forma privada y cifrada.',
    'upload.aiFeatureTitle': 'Extracción instantánea de vacante',
    'upload.aiFeatureBody':
      'Detectamos título del rol, responsabilidades, requisitos, rango salarial y más para acelerar la publicación.',
    'upload.unsupportedFormat': 'Formato no válido. Usa un archivo PDF o DOCX.',
    'upload.uploading': 'Subiendo y analizando: "{{name}}". Esto tomará unos segundos...',
    'upload.analysisComplete': '¡Análisis de IA completo! Cargando editor...',
    'upload.stepUploading': 'Subiendo archivo...',
    'upload.stepExtracting': 'Extrayendo texto...',
    'upload.stepAnalyzing': 'Analizando con IA...',
    'upload.stepExtractInfo': 'Extrayendo información de la vacante',
    'upload.stepAnalyzingIA': 'Analizando con IA',
    'upload.stepSaving': 'Guardando resultados',
    'upload.timeoutMessage':
      'El proceso está tomando más tiempo del esperado. Verifica en el panel si la vacante se creó correctamente.',
    'upload.analyzingDocument': 'Analizando documento',
    'upload.analyzingDocumentMessage':
      'Estamos analizando el documento para extraer los datos de la vacante.',
    'upload.analyzingShort': 'Un momento...',
    'upload.extractionComplete': '¡Procesamiento exitoso!',
    'upload.extractionCompleteMessage':
      'Hemos terminado de procesar el documento correctamente. Revisa los datos extraídos antes de publicar.',
    'upload.duplicateModalTitle': '¿Actualizar vacante existente?',
    'upload.duplicateModalMessage':
      'Ya tienes una vacante con el mismo título: «{{title}}». ¿Quieres actualizar esa vacante con este documento o conservar la vacante nueva que acabamos de crear?',
    'upload.duplicateOverwrite': 'Actualizar esa vacante',
    'upload.duplicateKeepNew': 'Conservar la nueva',
    'upload.errorAnalysis': 'Error en el análisis de IA.',
    'upload.timeoutLong':
      'El proceso está tomando más tiempo del esperado. Por favor, verifica en el panel de vacantes si la vacante se creó correctamente.',
    'upload.vacancyLimitReached':
      'Ya tienes el máximo de vacantes en tu plan. Cambia una vacante a estado Cerrado o actualiza tu plan para subir otra.',
    'upload.gcsUnavailable':
      'No se pudo guardar el archivo en el almacenamiento. Revisa la configuración de GCS en el servidor (credenciales y nombre del bucket).',
    'common.failed': 'Fallo',
    'common.unexpectedError': 'Error inesperado',
    'common.errorUpdate': 'Error al actualizar',
    // ──── Premium / Checkout ────
    'premium.title': 'Suscripción Premium',
    'premium.subtitle': 'Potencia tus procesos de reclutamiento con IA avanzada.',
    'premium.period': '/ mes',
    'premium.feature1': 'Análisis ilimitado de CVs',
    'premium.feature2': 'Entrevistas asistidas por IA',
    'premium.feature3': 'Reportes detallados de candidatos',
    'premium.feature4': 'Soporte prioritario',
    'premium.checkoutTitle': 'Finalizar Compra',
    'premium.cardInstructions': 'Introduce los datos de tu tarjeta',
    'premium.payNow': 'Pagar Ahora',
    'premium.paying': 'Procesando...',
    'premium.paid': '¡Pagado!',
    'premium.success': '¡Muchas gracias por tu compra!',
    'premium.errorInit': 'No se pudo inicializar el pago. Revisa la conexión.',
    'premium.errorPayment': 'Error en el pago: {{message}}',
    'premium.errorConfirm': 'No se pudo confirmar el pago con el servidor.',
    // ──── Layout / Sidebar ────
    'layout.expandMenu': 'Expandir menú',
    'layout.collapseMenu': 'Colapsar menú',
    'layout.themeDefault': 'Azul Clásico',
    'layout.themeViolet': 'Violeta',
    'layout.themeBlack': 'Negro puro',
    'layout.themeLightPure': 'Blanco puro',
    'layout.themeVioletInverted': 'Violeta invertido',
    'layout.notifications': 'Notificaciones',
    'layout.notificationsHeader': 'Notificaciones',
    'layout.markAllRead': 'Marcar todo como leído',
    'layout.noNotifications': 'No hay notificaciones',
    // ──── Settings – Notifications tab ────
    'settings.notifications.description':
      'Resumen de notificaciones. Cuando haya una nueva aplicación a una vacante tuya, aparecerá aquí con el detalle.',
    'settings.notifications.markAllRead': 'Marcar todo como leído',
    'settings.notifications.none': 'No hay notificaciones.',
    'settings.notifications.paginationShowing': 'Mostrando {from} - {to} de {total} notificaciones',
    'settings.notifications.vacancyLabel': 'Vacante: ',
    'settings.notifications.candidateLabel': 'Candidato: ',
    'settings.notifications.emailLabel': 'Email: ',
    'settings.profile.photoAlt': 'Foto de perfil',
    'recruiter.calendar.title': 'Google Calendar',
    'recruiter.calendar.hint':
      'Conecta la cuenta Google donde gestionas entrevistas. Es obligatorio para publicar vacantes y cruzar disponibilidad con candidatos.',
    'recruiter.calendar.checking': 'Verificando conexión…',
    'recruiter.calendar.connected': 'Calendario conectado',
    'recruiter.calendar.notConnected': 'Calendario no conectado',
    'recruiter.calendar.connectButton': 'Conectar Google Calendar',
    'recruiter.calendar.connecting': 'Conectando…',
    'recruiter.calendar.refresh': 'Verificar de nuevo',
    'recruiter.calendar.blockActivate':
      'Debes conectar Google Calendar en Ajustes antes de publicar la vacante.',
    'recruiter.calendar.activateTooltip': 'Conecta tu calendario en Ajustes para activar',
    'recruiter.calendar.statusError': 'No se pudo verificar el calendario. Intenta de nuevo.',
    'recruiter.calendar.schedulingPaused': 'Calendario conectado · entrevistas pausadas',
    'recruiter.calendar.pauseScheduling': 'Pausar entrevistas',
    'recruiter.calendar.resumeScheduling': 'Activar entrevistas',
    'recruiter.calendar.disconnect': 'Desconectar calendario',
    // ──── Onboarding ────
    'onboarding.step1Title': 'Bienvenido a AI Recruiter',
    'onboarding.step1Desc':
      'Tu plataforma inteligente para gestionar el proceso de reclutamiento con IA. Comienza creando tu primera vacante.',
    'onboarding.step2Title': '1. Carga tu Job Description',
    'onboarding.step2Desc':
      'Sube un archivo PDF o DOCX con la descripción del puesto. Nuestra IA extraerá automáticamente toda la información relevante.',
    'onboarding.step3Title': '2. Edita y Personaliza',
    'onboarding.step3Desc':
      'Revisa y ajusta los datos extraídos por la IA. Puedes modificar habilidades, requisitos, salario y más detalles del puesto.',
    'onboarding.step4Title': '3. Configura las Preguntas',
    'onboarding.step4Desc':
      'Personaliza las preguntas de la entrevista asistida por IA. Puedes agregar, editar o eliminar preguntas según tus necesidades.',
    'onboarding.step5Title': '4. Publica tu Vacante',
    'onboarding.step5Desc':
      'Cambia el estado de tu vacante a ACTIVO para que los candidatos puedan encontrarla y aplicar. Obtendrás un enlace público para compartir.',
    'onboarding.step6Title': '5. Revisa Candidatos',
    'onboarding.step6Desc':
      'En el Workspace podrás ver todos los candidatos que aplicaron, sus scores de idoneidad, análisis de IA y comparar perfiles fácilmente.',
    'onboarding.step7Title': '¡Listo para Empezar!',
    'onboarding.step7Desc':
      'Ahora conoces las funcionalidades principales. Haz clic en el botón para crear tu primera vacante y comenzar a reclutar con IA.',
    'onboarding.skip': 'Omitir',
    'onboarding.previous': 'Anterior',
    'onboarding.next': 'Siguiente',
    'onboarding.start': 'Comenzar',
    // ──── Upload Circle ────
    'uploadCircle.defaultText': 'Suelta tu archivo aquí',
    'uploadCircle.successText': '¡Enviado!',
    // ──── Workspace ────
    'workspace.loading': 'Calculando Scores de Idoneidad...',
    'workspace.pinnedProfileLabel': 'Perfil de {{name}}',
    'workspace.headerCandidate': 'Candidato',
    'workspace.headerStatus': 'Estado',
    'workspace.headerExp': 'Exp.',
    'workspace.headerSkills': 'Habilidades',
    'workspace.headerMatch': 'MATCH',
    'workspace.headerAction': 'Acción',
    'workspace.years': 'años',
    'workspace.contactLabel': 'Contacto:',
    'workspace.locationLabel': 'Ubicación:',
    'workspace.educationLabel': 'Educación:',
    'workspace.experienceLabel': 'Experiencia:',
    'workspace.certificationsLabel': 'Certificaciones:',
    'workspace.statusLabel': 'Estado:',
    'workspace.iaAnalysis': 'Análisis de IA',
    'workspace.experience': 'Experiencia',
    'workspace.technicalSkills': 'Habilidades Técnicas',
    'workspace.candidateResponses': 'Respuestas del Candidato',
    'workspace.clickToSeeAnswers': '(Haz clic para ver respuestas)',
    'workspace.softSkills': 'Habilidades Blandas',
    'workspace.education': 'Educación',
    'workspace.questionLabel': 'Pregunta {{num}}:',
    'workspace.answerLabel': 'Respuesta:',
    'workspace.noAnswers': 'No hay respuestas disponibles para este candidato.',
    'workspace.noBreakdown':
      'El desglose de compatibilidad no está disponible para este candidato. Puede estar en proceso de cálculo.',
    'workspace.detailTab.summary': 'Resumen IA',
    'workspace.detailTab.compatibility': 'Compatibilidad',
    'workspace.detailTab.cv': 'Curriculum Vitae (CV)',
    'workspace.detailTab.answers': 'Respuestas de preguntas con IA',
    'workspace.detailTabNavAria': 'Secciones del candidato expandido',
    'workspace.detailNoAiSummary': 'No hay análisis de IA disponible para este candidato.',
    'workspace.detailCvLoadError': 'No se pudo cargar el CV de este candidato.',
    'workspace.detailCvNoVacancy': 'Selecciona una vacante para ver el CV.',
    'workspace.detailAnswersLoading': 'Cargando respuestas…',
    'workspace.errorCouldNotDelete': 'No se pudo eliminar',
    'workspace.errorNetwork': 'Error de red',
    'workspace.errorLoadCandidates': 'No se pudo cargar la lista de candidatos.',
    'workspace.notScored': 'Sin evaluar',
    'workspace.estado.postulado': 'Postulado',
    'workspace.estado.en_revision': 'En revisión',
    'workspace.estado.entrevistado': 'Entrevistado',
    'workspace.estado.seleccionado': 'Seleccionado',
    'workspace.estado.descartado': 'Descartado',
    'workspace.errorEstado': 'No se pudo cambiar el estado del candidato.',
    'workspace.closeProcess': 'Cerrar proceso',
    'workspace.closeProcessHint':
      'Cierra la vacante, contrata a quien tengas seleccionado y descarta al resto.',
    'workspace.closing': 'Cerrando...',
    'workspace.closeConfirmHired':
      'Vas a contratar a {{name}} y a descartar a {{count}} candidato(s) que siguen en proceso. La vacante quedará cerrada. ¿Continuar?',
    'workspace.closeConfirmNoHire':
      'No tienes a nadie seleccionado. Se descartará a {{count}} candidato(s) y la vacante quedará cerrada sin contratar. ¿Continuar?',
    'workspace.closeDone': 'Proceso cerrado. Se descartaron {{count}} candidatura(s).',
    'workspace.errorClose': 'No se pudo cerrar el proceso.',
    'workspace.notScoredHint': 'Se ha postulado, pero la IA todavía no lo ha evaluado.',
    // ──── Metrics ────
    'metrics.loading': 'Cargando métricas...',
    'metrics.errorLoading': 'Error al cargar métricas',
    'metrics.pageTitle': 'Métricas de Selección',
    'metrics.viewAllInWorkspace': 'Ver todos en panel',
    'metrics.viableThreshold': 'score ≥ 70%',
    'metrics.noDataTitle': 'Sin datos aún',
    'metrics.noDataDesc':
      'Las métricas se mostrarán cuando haya candidatos postulados a esta vacante.',
    'metrics.totalCandidates': 'Total Candidatos',
    'metrics.applicationsReceived': 'aplicaciones recibidas',
    'metrics.avgScore': 'Score Promedio',
    'metrics.avgMatch': 'idoneidad promedio',
    'metrics.topScore': 'Score Más Alto',
    'metrics.bestCandidate': 'mejor candidato',
    'metrics.viableCandidates': 'Candidatos Viables',
    'metrics.talentQuality': 'Calidad del Talento',
    'metrics.candidates': 'candidatos',
    'metrics.candidatesInRange': 'Candidatos en rango',
    'metrics.generalMatch': 'Match General',
    'metrics.weight': 'Peso',
    'metrics.candidateSource': 'Origen de Candidatos',
    'metrics.avgScoreLabel': 'Score Promedio',
    'metrics.experienceLevel': 'Experiencia',
    'metrics.keySkills': 'Habilidades Clave',
    'metrics.notEnoughData': 'Sin datos suficientes',
    'metrics.topScoreMentions': 'menciones en top scores',
    'metrics.frequencyNote': 'Frecuencia en candidatos con score > 75%',
    'metrics.educationTitle': 'Educación',
    'metrics.notSpecified': 'Sin especificar',
    // ──── Metrics – Radar categories ────
    'metrics.radar.experience': 'Experiencia',
    'metrics.radar.technicalSkills': 'Hab. Técnicas',
    'metrics.radar.responses': 'Respuestas',
    'metrics.radar.softSkills': 'Hab. Blandas',
    'metrics.radar.education': 'Educación',
    // ──── Metrics – Education labels ────
    'metrics.edu.bachelors': 'Licenciatura',
    'metrics.edu.masters': 'Maestría',
    'metrics.edu.doctorate': 'Doctorado',
    'metrics.edu.certifications': 'Certificaciones',
    'metrics.edu.other': 'Otros',
    // ──── Recruiter CV form ────
    'recruiter.cv.profileTitle': 'Perfil: {{name}}',
    'recruiter.cv.profileTitleFallback': 'Perfil del candidato',
    'recruiter.cv.readOnlySubtitle':
      'Consulta la información del candidato. Solo el candidato puede modificar su perfil.',
    'recruiter.cv.editSubtitleBefore': 'Revisa y edita la información. Usa los botones',
    'recruiter.cv.editSubtitleAfter': 'para completar secciones.',
    'recruiter.cv.loadingTitle': 'Cargando perfil del candidato...',
    'recruiter.cv.loadingSubtitle': 'Obteniendo la información extraída del CV.',
    'recruiter.cv.saveChanges': 'Guardar cambios',
    'recruiter.cv.saved': '✓ Guardado',
    // ──── Candidate Flow ────
    'candidate.default': 'Candidato',
    'candidate.loading': 'Cargando...',
    'candidate.expandMenu': 'Expandir menú',
    'candidate.collapseMenu': 'Colapsar menú',
    'layout.changeLanguage': 'Cambiar Idioma',
    'candidate.switchToEnglish': 'Switch to English',
    'candidate.switchToSpanish': 'Cambiar a Español',
    'candidate.logout': 'Cerrar sesión',
    // Step 1 – CV Upload
    'candidate.step1.title': 'Sube tu CV',
    'candidate.step1.description': 'Arrastra tu archivo CV aquí o haz clic para seleccionarlo',
    'candidate.step1.formats': 'Formatos aceptados:',
    'candidate.step1.dragHere': 'Arrastra tu CV aquí',
    'candidate.step1.cvProcessed': 'CV procesado',
    'candidate.step1.stepsTitle': 'Pasos del Proceso',
    'candidate.step1.stepsDescription':
      'Completa los siguientes requerimientos para postularte a esta vacante:',
    'candidate.step1.uploadCV': 'Subir tu CV actualizado',
    'candidate.step1.verifyInfo': 'Verificar información extraída',
    'candidate.step1.recordVideo': 'Graba un video de presentación',
    'candidate.step1.answerQuestions': 'Responde preguntas de la vacante',
    'candidate.step1.linkCalendar': 'Vincular tu calendario de Google',
    'candidate.step1.scheduleInterview': 'Agendar día y hora de entrevista',
    'candidate.step1.savedCvPrompt': 'Ya tienes un CV guardado ({name}). ¿Qué prefieres hacer?',
    'candidate.step1.useSavedCv': 'Usar mi CV guardado',
    'candidate.step1.uploadNewForVacancy': 'Subir uno nuevo para esta vacante',
    'candidate.step1.selectVacancyFirst': 'Selecciona una vacante antes de usar el CV guardado.',
    'candidate.workspace.title': 'Mi espacio de candidato',
    'candidate.workspace.navAria': 'Navegación del candidato',
    'candidate.workspace.dashboard': 'Dashboard',
    'candidate.workspace.applications': 'Mis postulaciones',
    'candidate.workspace.jobs': 'Vacantes',
    'candidate.workspace.documents': 'Mis documentos',
    'candidate.workspace.settings': 'Configuración',
    'candidate.workspace.loading': 'Cargando…',
    'candidate.workspace.continueApplication': 'Postulación en curso',
    'candidate.workspace.continueBtn': 'Continuar',
    'candidate.workspace.summary': 'Resumen',
    'candidate.workspace.summaryStats':
      '{{applications}} postulación(es) · {{jobs}} vacante(s) disponible(s)',
    'candidate.workspace.noApplications': 'Aún no tienes postulaciones.',
    'candidate.workspace.noJobs': 'No hay vacantes disponibles por ahora.',
    'candidate.workspace.resolved': 'Proceso resuelto',
    'candidate.workspace.submitted': 'Enviada, en revisión',
    'candidate.workspace.closedVacancy': 'Vacante cerrada',
    'candidate.workspace.minExperience': '{{years}} año(s) de experiencia',
    'candidate.workspace.noMinExperience': 'Sin experiencia mínima',
    'candidate.workspace.applyBtn': 'Postularme',
    'candidate.workspace.paginationShowing': 'Mostrando {from} - {to} de {total} postulaciones',
    'candidate.jobs.subtitle': 'Vacantes a las que aún no te has postulado.',
    'candidate.jobs.openCount': '{{count}} abiertas',
    'candidate.jobs.searchPlaceholder': 'Buscar por título, resumen o habilidad…',
    'candidate.jobs.foundCount': '{{count}} encontradas',
    'candidate.jobs.quickFilters': 'Filtros rápidos',
    'candidate.jobs.filterExperience': 'Experiencia mínima',
    'candidate.jobs.filterSort': 'Orden',
    'candidate.jobs.filterSkills': 'Habilidades',
    'candidate.jobs.experienceAll': 'Todas',
    'candidate.jobs.experienceNone': 'Sin mínimo',
    'candidate.jobs.experienceUpTo2': 'Hasta 2 años',
    'candidate.jobs.experienceThreePlus': '3 años o más',
    'candidate.jobs.sortTitleAsc': 'A → Z',
    'candidate.jobs.sortTitleDesc': 'Z → A',
    'candidate.jobs.sortExpAsc': 'Menor experiencia exigida',
    'candidate.jobs.sortExpDesc': 'Mayor experiencia exigida',
    'candidate.jobs.clearFilters': 'Limpiar filtros',
    'candidate.jobs.clearSearch': 'Limpiar búsqueda',
    'candidate.jobs.noResults': 'No encontramos vacantes con estos criterios.',
    'candidate.jobs.goToDocuments': 'Ir a mis documentos',
    'candidate.jobs.masterTitle': 'Vacantes disponibles',
    'candidate.jobs.skillsTitle': 'Habilidades clave',
    'candidate.jobs.noSummary': 'Sin descripción disponible.',
    'candidate.jobs.tabList': 'Lista ({{count}})',
    'candidate.jobs.tabDetail': 'Detalle',
    'candidate.jobs.paginationShowing': 'Mostrando {from} - {to} de {total} vacantes',
    'candidate.dashboard.portalBadge': 'Portal del candidato',
    'candidate.dashboard.subtitleAttention':
      'Tienes {{count}} postulación(es) en curso que requieren tu atención.',
    'candidate.dashboard.subtitleDefault': 'Resumen de tu actividad en el portal.',
    'candidate.dashboard.kpiTotal': 'Total postulaciones',
    'candidate.dashboard.kpiInProgress': 'En curso',
    'candidate.dashboard.kpiCompleted': 'Completadas',
    'candidate.dashboard.kpiOpenJobs': 'Vacantes abiertas',
    'candidate.dashboard.kpiSubTotal': 'Registradas en tu cuenta',
    'candidate.dashboard.kpiSubInProgress': 'Borradores o procesos activos',
    'candidate.dashboard.kpiSubCompleted': 'Procesos archivados o cerrados',
    'candidate.dashboard.kpiSubOpenJobs': 'Disponibles para postular',
    'candidate.dashboard.sectionInProgress': 'Postulaciones en curso',
    'candidate.dashboard.activeBadge': '{{count}} activas',
    'candidate.dashboard.continueApplication': 'Continuar postulación',
    'candidate.dashboard.viewDetail': 'Ver detalle',
    'candidate.dashboard.viewAllApplications': 'Ver todas las postulaciones',
    'candidate.dashboard.showingInProgress': 'Mostrando {{shown}} de {{total}} en curso',
    'candidate.dashboard.noActivePending': 'Sin postulaciones activas pendientes',
    'candidate.dashboard.exploreHint':
      'Explora las vacantes abiertas y postúlate a las que encajen con tu perfil.',
    'candidate.dashboard.suggestedJobsBadge': '{{count}} vacantes sugeridas',
    'candidate.dashboard.exploreCatalog': 'Ver todas en la bolsa de empleo',
    'candidate.dashboard.quickAccess': 'Accesos rápidos',
    'candidate.dashboard.quickApplicationsDesc': 'Historial y estados',
    'candidate.dashboard.quickJobsDesc': 'Vacantes abiertas',
    'candidate.dashboard.quickDocumentsDesc': 'CV reutilizable',
    'candidate.dashboard.cvStatusOk': 'Al día',
    'candidate.dashboard.onboardingTitle': 'Aún no tienes postulaciones registradas',
    'candidate.dashboard.onboardingBody':
      'Explora vacantes abiertas o sube tu CV para reutilizarlo en futuras postulaciones.',
    'candidate.dashboard.exploreJobs': 'Explorar vacantes',
    'candidate.dashboard.uploadCv': 'Cargar currículum',
    'candidate.dashboard.loadError': 'No se pudo cargar tu resumen.',
    'candidate.dashboard.retry': 'Reintentar',
    'candidate.dashboard.chipCv': 'CV',
    'candidate.dashboard.chipVideo': 'Video',
    'candidate.documents.empty': 'Aún no has subido un CV reutilizable.',
    'candidate.documents.uploadBtn': 'Subir CV (PDF o DOCX)',
    'candidate.documents.uploading': 'Subiendo…',
    'candidate.documents.uploadError': 'No se pudo subir el CV.',
    'candidate.documents.deleteError': 'No se pudo eliminar el CV.',
    'candidate.documents.unnamed': 'CV.pdf',
    'candidate.documents.uploadedAt': 'Subido: {{date}}',
    'candidate.documents.replaceBtn': 'Actualizar archivo',
    'candidate.documents.deleteBtn': 'Eliminar',
    'candidate.documents.deleteTitle': 'Eliminar CV guardado',
    'candidate.documents.deleteMessage':
      'Se quitará tu CV global. Las postulaciones ya enviadas no se modifican.',
    'candidate.documents.deleteConfirm': 'Eliminar',
    'candidate.documents.badgeNoCv': 'Sin CV guardado',
    'candidate.documents.badgeHasCv': 'CV guardado',
    'candidate.documents.pageSubtitle':
      'Sube un CV para reutilizarlo cuando postules a las ofertas de empleo.',
    'candidate.documents.tabMainFile': 'Archivo principal',
    'candidate.documents.tabHowItWorks': '¿Cómo funciona?',
    'candidate.documents.mainSlotLabel': 'Espacio de CV principal',
    'candidate.documents.formatsHint': 'Formatos recomendados: PDF, DOCX',
    'candidate.documents.dropHint':
      'Arrastra y suelta tu archivo aquí o haz clic para explorar en tu dispositivo.',
    'candidate.documents.selectFromDevice': 'Seleccionar archivo desde tu equipo',
    'candidate.documents.bulletReusable': 'Reutilizable automáticamente en nuevas postulaciones',
    'candidate.documents.bulletFormats': 'Formatos permitidos: PDF y DOCX',
    'candidate.documents.bulletApplications': 'Las postulaciones ya enviadas no se modificarán',
    'candidate.documents.howItWorksTitle': '¿Cómo funciona?',
    'candidate.documents.howItWorksLead': 'Tu CV global centralizado en 3 sencillos pasos.',
    'candidate.documents.step1Title': 'Subes tu CV una vez aquí',
    'candidate.documents.step1Desc': 'Lo almacenamos de forma segura en tu perfil.',
    'candidate.documents.step2Title': 'Al postular puedes reutilizarlo',
    'candidate.documents.step2Desc': 'Ahorra tiempo autocompletando tus datos en un solo clic.',
    'candidate.documents.step3Title': 'Puedes actualizarlo cuando quieras',
    'candidate.documents.step3Desc':
      'Cambia tu CV en cualquier momento sin afectar postulaciones previas.',
    'candidate.documents.goToJobs': 'Ir a vacantes disponibles',
    'candidate.settings.theme': 'Tema visual',
    'candidate.settings.language': 'Idioma',
    'candidate.settings.subtitle': 'Personaliza apariencia e idioma de tu portal',
    'candidate.settings.appearanceDesc':
      'Elige la paleta visual para las tarjetas de vacantes, el flujo de postulación y las vistas de tu portal.',
    'candidate.settings.languageDesc': 'Configura el idioma de la interfaz del postulante.',
    'candidate.settings.languagePlatformLabel': 'Idioma de la plataforma',
    'candidate.settings.languageHeaderHelper':
      'También puedes cambiar el idioma desde el icono de globo en el encabezado en cualquier momento.',
    'candidate.settings.activeThemePreview': 'Tema activo: {{name}}',
    'candidate.settings.themesAvailable': 'Temas disponibles',
    'candidate.settings.mobileTabTheme': 'Tema y color',
    'candidate.settings.mobileTabLanguage': 'Idioma',
    'candidate.application.sectionInProgress': 'En curso',
    'candidate.application.sectionCompletedClosed': 'Completadas / cerradas',
    'candidate.application.emptyInProgress': 'No tienes postulaciones en curso.',
    'candidate.application.emptyCompleted': 'No tienes postulaciones completadas o cerradas.',
    'candidate.application.totalProcesses': '{{count}} procesos registrados',
    'candidate.application.activeBadge': '{{count}} activas',
    'candidate.application.actionRequired': 'Acción requerida',
    'candidate.application.viewDetail': 'Ver detalle',
    'candidate.application.chipCvDone': 'CV ✓',
    'candidate.application.chipCvPending': 'CV pendiente',
    'candidate.application.chipVideoDone': 'Video ✓',
    'candidate.application.chipVideoPending': 'Video pendiente',
    'candidate.application.exploreJobs': 'Explorar vacantes abiertas',
    'candidate.application.loadError': 'No pudimos cargar tus postulaciones.',
    'candidate.application.retry': 'Reintentar',
    'candidate.application.backToList': 'Volver a mis postulaciones',
    'candidate.application.notFound': 'No encontramos esta postulación.',
    'candidate.application.timelineTitle': 'Estado del proceso',
    'candidate.application.checklistTitle': 'Entregables',
    'candidate.application.draftInProgressTitle': 'Borrador en curso',
    'candidate.application.draftLastSaved':
      'Último guardado hace {{minutes}}min · Paso {{step}} de 5',
    'candidate.application.resumeDraft': 'Continuar donde me quedé',
    'candidate.application.checklistCv': 'CV',
    'candidate.application.checklistVideo': 'Video',
    'candidate.application.timeline.postulado': 'Postulado',
    'candidate.application.timeline.en_revision': 'En revisión',
    'candidate.application.timeline.entrevistado': 'Entrevistado',
    'candidate.application.timeline.seleccionado': 'Seleccionado',
    'candidate.application.timeline.descartado': 'Descartado',
    // Step Review Data
    'candidate.review.analyzingTitle': 'Analizando Perfil...',
    'candidate.review.analyzingSubtitle': 'Estamos organizando la información extraída de tu CV.',
    'candidate.review.title': 'Validación de Perfil',
    'candidate.review.subtitle':
      'Revisa que la información extraída sea correcta. Haz clic en cualquier campo para editarlo.',
    'candidate.review.personalInfo': 'Información Personal',
    'candidate.review.fullName': 'Nombre Completo',
    'candidate.review.fullNamePlaceholder': 'Tu nombre',
    'candidate.review.email': 'Email Profesional',
    'candidate.review.phone': 'Teléfono de Contacto',
    'candidate.review.profileSummary': 'Resumen de Perfil',
    'candidate.review.profileSummaryPlaceholder': 'Breve descripción de tu trayectoria...',
    'candidate.review.vacancySource': '¿Cómo te enteraste de esta vacante?',
    'candidate.review.selectOption': 'Selecciona una opción',
    'candidate.review.referred': 'Referido / Conocido',
    'candidate.review.jobBoard': 'Bolsa de Trabajo (OCC, CompuTrabajo, etc.)',
    'candidate.review.companyWebsite': 'Sitio Web de la empresa',
    'candidate.review.socialNetworks': 'Redes Sociales (Facebook, Instagram, X)',
    'candidate.review.university': 'Universidad / Escuela',
    'candidate.review.other': 'Otro',
    'candidate.review.workExperience': 'Experiencia Laboral',
    'candidate.review.jobTitle': 'Cargo / Posición',
    'candidate.review.company': 'Empresa',
    'candidate.review.period': 'Periodo',
    'candidate.review.start': 'Inicio',
    'candidate.review.end': 'Fin',
    'candidate.review.location': 'Ubicación',
    'candidate.review.responsibilities': 'Responsabilidades y Logros',
    'candidate.review.responsibilitiesPlaceholder': 'Una responsabilidad por línea',
    'candidate.review.education': 'Educación',
    'candidate.review.degree': 'Título / Grado Académico',
    'candidate.review.institution': 'Institución Universitaria / Escuela',
    'candidate.review.projects': 'Proyectos Destacados',
    'candidate.review.projectName': 'Nombre del Proyecto',
    'candidate.review.technologies': 'Tecnologías Utilizadas',
    'candidate.review.projectDescription': 'Descripción del Proyecto',
    'candidate.review.skills': 'Habilidades',
    'candidate.review.languages': 'Idiomas',
    'candidate.review.certifications': 'Certificaciones',
    'candidate.review.certName': 'Nombre de la Certificación',
    'candidate.review.certIssuer': 'Entidad Emisora',
    'candidate.review.saving': '⏳ Guardando...',
    'candidate.review.confirmed': '✓ ¡Datos Confirmados!',
    'candidate.review.confirmData': 'Confirmar Datos',
    // Step 2 – Video Recorder
    'candidate.video.prepTitle': 'Prepárate para tu video presentación',
    'candidate.video.prepSubtitle':
      'Explica brevemente tu experiencia y por qué te interesa esta posición',
    'candidate.video.lighting': 'Iluminación',
    'candidate.video.lightingTip':
      'Asegúrate de estar en un lugar bien iluminado, preferiblemente con luz de frente.',
    'candidate.video.sound': 'Sonido',
    'candidate.video.soundTip':
      'Busca un entorno silencioso para que podamos escucharte con claridad.',
    'candidate.video.framing': 'Encuadre',
    'candidate.video.framingTip':
      'Coloca la cámara a la altura de tus ojos y mantén una distancia cómoda.',
    'candidate.video.naturalness': 'Naturalidad',
    'candidate.video.naturalnessTip':
      'No te preocupes por ser perfecto, sé tú mismo. Estamos emocionados de escucharte.',
    'candidate.video.start': 'Comenzar',
    'candidate.video.recordingTitle': 'Grabación de Entrevista',
    'candidate.video.cameraPreview': 'Previsualización de la cámara',
    'candidate.video.startRecording': 'Iniciar Grabación',
    'candidate.video.stopRecording': 'Detener Grabación',
    'candidate.video.uploadRecorded': 'Subir Video Grabado',
    'candidate.video.downloadVideo': 'Descargar Video',
    'candidate.video.uploadSuccess': '¡Video subido con éxito!',
    'candidate.video.uploading': 'Subiendo video...',
    'candidate.video.or': 'o',
    'candidate.video.uploadOwn': 'Sube tu Propio Video',
    'candidate.video.selectVideo': 'Seleccionar Video',
    'candidate.video.selectedFile': 'Archivo seleccionado: {{name}}',
    'candidate.video.uploadFile': 'Subir Archivo',
    // Step 3 – Questions
    'candidate.questions.title': 'Entrevista Asistida por IA',
    'candidate.questions.selectVacancy': 'Selecciona la vacante a la que deseas aplicar:',
    'candidate.questions.loadingVacancies': 'Cargando vacantes disponibles...',
    'candidate.questions.noVacancies': 'No se encontraron vacantes disponibles.',
    'candidate.questions.selectThis': 'Seleccionar Esta Vacante',
    'candidate.questions.yearsExp': '{{years}}+ años',
    'candidate.questions.answerPlaceholder': 'Escribe tu respuesta aquí...',
    'candidate.questions.charCount': '{{count}} / {{max}} caracteres',
    'candidate.questions.shortAnswerHint': 'Una respuesta más detallada se evalúa mejor.',
    'candidate.questions.preparingInterview': 'Preparando tu entrevista personalizada...',
    'candidate.questions.answerInstructions':
      'Responde a las siguientes preguntas para completar tu entrevista:',
    'candidate.questions.changeVacancy': 'Cambiar Vacante',
    'candidate.questions.salaryLabel': '¿Cuál es tu expectativa salarial neta mensual?',
    'candidate.questions.salaryPlaceholder': 'Ej: $25,000 netos mensuales',
    'candidate.questions.salaryHint': 'Indica el monto neto mensual en pesos mexicanos (MXN)',
    'candidate.questions.submit': 'Enviar Respuestas',
    'candidate.questions.incompleteAlert': 'Contesta todas las preguntas para continuar.',
    'candidate.questions.errorSave': 'Error al guardar respuestas: {{error}}',
    'candidate.questions.noQuestions': 'No se pudieron obtener las preguntas para esta vacante.',
    'candidate.questions.pendingReview':
      'Las preguntas de esta vacante están siendo revisadas por el reclutador. Por favor, intenta más tarde.',
    'candidate.questions.rejected':
      'Las preguntas de esta vacante fueron rechazadas. El reclutador debe generar nuevas preguntas.',
    'candidate.questions.checkConnection': 'Verifica la conexión o intenta de nuevo.',
    'candidate.questions.tryAgain': 'Intentar De Nuevo',
    // Step 4 – Calendar
    'candidate.calendar.verifying': 'Verificando acceso al calendario...',
    'candidate.calendar.processing': 'Procesando Conexión...',
    'candidate.calendar.processingMessage':
      'Estamos confirmando la conexión con Google. Por favor, espera un momento, esto puede tardar unos segundos.',
    'candidate.calendar.connectTitle': 'Conecta tu Calendario para Entrevistas',
    'candidate.calendar.connectMessage':
      'Conecta tu Google Calendar para que podamos ver tu disponibilidad y enviarte invitaciones de entrevista.',
    'candidate.calendar.connectButton': 'Conectar Google Calendar',
    'candidate.calendar.loadingUser': 'Cargando Usuario...',
    'candidate.calendar.waitingAuth':
      'Esperando la autenticación de Firebase para obtener tu ID de usuario...',
    'candidate.calendar.errorConnect':
      'Error al conectar con Google Calendar. Por favor, inténtalo de nuevo.',
    'candidate.calendar.errorRetry': 'Error: {{error}}. Por favor, inténtalo de nuevo.',
    'candidate.scheduling.prepareTitle': 'Preparar tu agenda',
    'candidate.scheduling.prepareSubtitle':
      'Revisa el estado de tu Google Calendar y la agenda de la empresa antes de elegir horario.',
    'candidate.scheduling.row.candidate': 'Tu Google Calendar',
    'candidate.scheduling.row.candidateHint':
      'Necesitamos permiso de lectura para cruzar tu disponibilidad.',
    'candidate.scheduling.row.company': 'Agenda de la empresa',
    'candidate.scheduling.row.companyHint':
      'La empresa confirma disponibilidad del reclutador asignado.',
    'candidate.scheduling.status.pending': 'Pendiente',
    'candidate.scheduling.status.ready': 'Listo',
    'candidate.scheduling.status.paused': 'Pausada',
    'candidate.scheduling.status.unavailable': 'No disponible',
    'candidate.scheduling.status.checking': 'Verificando…',
    'candidate.scheduling.whyAccess': '¿Por qué pedimos acceso?',
    'candidate.scheduling.whyModal.title': '¿Por qué pedimos acceso a tu calendario?',
    'candidate.scheduling.whyModal.body':
      'Solo usamos tu calendario para mostrar franjas libres y enviarte invitaciones de entrevista. No publicamos eventos sin tu confirmación.',
    'candidate.scheduling.whyModal.privacyLink': 'Ver aviso de privacidad',
    'candidate.scheduling.waitRoom.title': 'Aún no puedes elegir horario',
    'candidate.scheduling.waitRoom.retry': 'Reintentar',
    'candidate.scheduling.waitRoom.retrying': 'Reintentando…',
    'candidate.scheduling.waitRoom.generic': 'Estamos esperando que ambas agendas estén listas.',
    'candidate.scheduling.waitRoom.recruiterNotConnected':
      'El reclutador aún no ha conectado su calendario. Te avisaremos cuando puedas agendar.',
    'candidate.scheduling.waitRoom.recruiterPaused':
      'El reclutador pausó temporalmente las entrevistas. Vuelve a intentar en unos minutos.',
    'candidate.scheduling.waitRoom.recruiterUnavailable':
      'No pudimos verificar la agenda del reclutador. Intenta de nuevo pronto.',
    'candidate.scheduling.waitRoom.candidateNotConnected':
      'Conecta tu Google Calendar en el paso anterior para continuar.',
    'candidate.scheduling.waitRoom.candidateUnavailable':
      'Vuelve a conectar tu calendario; no pudimos leer tu disponibilidad.',
    // Step 5 – Calendar Success
    'candidate.calendarSuccess.title': 'Calendario Conectado Exitosamente',
    'candidate.calendarSuccess.message':
      'Gracias por conectar tu Google Calendar. Ya podemos verificar tu disponibilidad para futuras entrevistas.',
    'candidate.calendarSuccess.errorTitle': 'Falló la Conexión del Calendario',
    'candidate.calendarSuccess.errorDetail': 'Detalle del Error:',
    'candidate.calendarSuccess.errorHint':
      'Por favor, inténtalo de nuevo. Asegúrate de otorgar todos los permisos solicitados por Google.',
    'candidate.calendarSuccess.defaultError': 'Ocurrió un error desconocido durante la conexión.',
    'candidate.calendarSuccess.back': 'Volver al inicio',
    'candidate.calendarSuccess.pickSchedule': 'Elegir horario',
    'candidate.calendarSuccess.goHome': 'Ir al inicio',
    'candidate.calendarSuccess.retryConnect': 'Reintentar conexión',
    // Step 6 – Schedule Interview
    'candidate.schedule.title': 'Agendar Entrevista',
    'candidate.schedule.description': 'Selecciona un día y horario disponible para tu entrevista.',
    'candidate.schedule.queryingTimes': 'Consultando horarios disponibles...',
    'candidate.schedule.notice': 'Aviso:',
    'candidate.schedule.reconnect': 'Re-conectar Google Calendar',
    'candidate.schedule.fallbackHint': 'Puedes elegir día y hora a continuación.',
    'candidate.schedule.recruiterCalendarWarning':
      'No pudimos verificar la agenda del reclutador; los horarios mostrados pueden confirmarse o ajustarse después.',
    'candidate.schedule.recruiterCalendarNotConnected':
      'El reclutador aún no ha conectado su calendario; los horarios pueden no coincidir con su disponibilidad real.',
    'candidate.schedule.selectDay': 'Selecciona un día:',
    'candidate.schedule.availableTimes': 'Horarios disponibles:',
    'candidate.schedule.noSlots': 'No hay horarios disponibles para este día.',
    'candidate.schedule.selected': 'Has seleccionado: {{date}} a las {{time}}',
    'candidate.schedule.confirm': 'Confirmar Entrevista',
    'candidate.schedule.scheduling': 'Agendando...',
    'candidate.schedule.processingTitle': 'Procesando tu cita...',
    'candidate.schedule.processingMessage':
      'Estamos agendando tu entrevista y enviando las invitaciones correspondientes.',
    'candidate.schedule.completedTitle': '¡Aplicación Finalizada!',
    'candidate.schedule.completedMessage':
      'Has completado todos los pasos de tu postulación con éxito.',
    'candidate.schedule.proposedTime': 'Horario Propuesto para Entrevista',
    'candidate.schedule.pendingValidation':
      'El reclutador revisará tu perfil y, en caso de ser viable, se confirmará la entrevista vía Google Meet. Recibirás una notificación por correo electrónico. La cita puede estar sujeta a cambios de acuerdo a la disponibilidad del reclutador.',
    'candidate.schedule.thanks': '¡Gracias por aplicar! Te deseamos mucho éxito.',
    'candidate.schedule.applyOther': 'Aplicar a otra vacante',
    'candidate.schedule.finishProcess': 'Finalizar proceso',
    'candidate.schedule.exploreOther': 'Explora otras vacantes',
    'candidate.schedule.exploreMessage':
      'Aún puedes aplicar a otras posiciones que coincidan con tu perfil:',
    'candidate.schedule.backConfirmation': 'Volver a mi confirmación',
    'candidate.schedule.authError': 'Error de autenticación. Por favor, reinicia la sesión.',
    'candidate.schedule.selectTime': 'Por favor, selecciona un horario.',
    'candidate.schedule.tokenNotFound': 'Token de sesión no encontrado.',
    'candidate.schedule.errorSchedule': 'Error al agendar la entrevista',
    'candidate.schedule.errorScheduleAlert': 'Error al agendar entrevista: {{error}}',
    'candidate.schedule.mustAuth': 'Debes estar autenticado para conectar tu calendario.',
    'candidate.schedule.errorQueryAvailability': 'Fallo al consultar disponibilidad:',
    'candidate.schedule.errorFetchAvailability': 'Error al obtener disponibilidad.',
    'candidate.schedule.calendarReconnectRequired':
      'No se pudo consultar tu calendario. Vuelve a conectar Google Calendar.',
    'candidate.schedule.retryFetch': 'Reintentar',
    'candidate.schedule.confirmBlockedRecruiterCalendar':
      'No puedes confirmar hasta que el reclutador conecte su calendario.',
    'candidate.schedule.errors.recruiterCalendarNotConnected':
      'El reclutador aún no ha conectado su calendario. Intenta más tarde.',
    'candidate.schedule.errors.recruiterCalendarUnavailable':
      'No se pudo verificar la agenda del reclutador. Intenta más tarde.',
    'candidate.schedule.errors.slotUnavailable': 'Ese horario ya no está disponible. Elige otro.',
    'candidate.schedule.errors.candidateCalendarUnavailable':
      'No se pudo consultar tu calendario. Vuelve a conectar Google Calendar.',
    // Stepper
    'candidate.stepper.verifyingSession': 'Verificando sesión...',
    'candidate.stepper.loadingSession': 'Cargando sesión de usuario...',
    'candidate.stepper.processTitle': 'Proceso de Aplicación',
    'candidate.stepper.processDescription':
      'Completa cada uno de estos 5 pasos para potenciar tu postulación profesional',
    'candidate.stepper.startProcess': 'Comenzar Proceso',
    'candidate.stepper.previous': 'Anterior',
    'candidate.stepper.previousStep': 'Paso anterior',
    'candidate.stepper.reviewLabel': 'Revisión',
    'candidate.stepper.interviewLabel': 'Entrevista',
    'candidate.stepper.scheduleLabel': 'Agendar',
    'candidate.stepper.errorAuth': 'Error: Usuario no autenticado',
    'candidate.stepper.errorUploadCV': 'Error al subir el CV: {{error}}',
    'candidate.stepper.errorExtractCV':
      'Tu CV se guardó, pero no pudimos leerlo automáticamente: {{error}}. Inténtalo de nuevo en unos minutos.',
    'candidate.stepper.errorExtractCVUnknown': 'el servicio de lectura no está disponible',
    'candidate.stepper.alreadyApplied':
      'Ya te postulaste a esta vacante el {{date}}. Si continúas, reemplazarás tu CV, tu video y tus respuestas anteriores. ¿Quieres continuar?',
    'candidate.stepper.alreadyResolved':
      'Esta vacante ya se resolvió y tu candidatura está como "{{estado}}". Ya no se pueden enviar cambios.',
    'candidate.stepper.previousInProgress':
      'Ya te postulaste a esta vacante el {{date}}. Estado: {{estado}}. Si vuelves a enviar, reemplazarás lo anterior.',
    'candidate.stepper.previousResolved':
      'Esta vacante ya se resolvió. Tu candidatura quedó como: {{estado}}.',
    'candidate.stepper.seeOtherVacancies': 'Ver otras vacantes',
    'candidate.stepper.errorUploadVideo': 'Error al subir el video: {{error}}',
    'candidate.stepper.errorConfirmData': 'Error al confirmar datos: {{error}}',
    'candidate.stepper.processingSuccess': '¡Procesamiento Exitoso!',
    'candidate.stepper.processingSuccessMessage':
      'Hemos terminado de procesar tu información correctamente.',
    'candidate.stepper.confirmingProfile': 'Confirmando Perfil',
    'candidate.stepper.confirmingProfileMessage':
      'Validando tus datos profesionales para el siguiente paso.',
    'candidate.stepper.sendingAnswers': 'Enviando Respuestas',
    'candidate.stepper.sendingAnswersMessage':
      'Estamos procesando tu entrevista para el reclutador.',
    'candidate.stepper.processingCV': 'Procesando tu CV',
    'candidate.stepper.processingCVMessage':
      'Estamos analizando tu trayectoria para agilizar tu postulación profesional.',
    'candidate.stepper.processingVideo': 'Procesando tu Video',
    'candidate.stepper.processingVideoMessage':
      'Estamos analizando tu video para evaluar tus habilidades y competencias.',
    'candidate.stepper.cvStep1': 'Subiendo CV a nuestros servidores',
    'candidate.stepper.cvStep2': 'Extrayendo información profesional con IA',
    'candidate.stepper.cvStep3': 'Estructurando perfil de candidato',
    'candidate.stepper.videoStep1': 'Subiendo video',
    'candidate.stepper.videoStep2': 'Analizando sentimientos y emociones',
    'candidate.stepper.videoStep3': 'Evaluando comunicación verbal y no verbal',
    'candidate.stepper.videoStep4': 'Calculando scoring y guardando resultados',
    'candidate.stepper.stepBadge': 'Paso {{current}} de {{total}}',
    'candidate.stepper.continue': 'Continuar',
    'candidate.stepper.footerUploadHint':
      'Sube tu CV o usa el guardado en tu cuenta para continuar.',
    'candidate.stepper.footerStartHint': 'Inicia el proceso para continuar.',
    'candidate.stepper.footerVideoHint': 'Graba o sube tu video para continuar.',
    'candidate.stepper.step1AiBanner':
      'Nuestro motor de IA analizará tu CV para autocompletar tu perfil en el paso de revisión.',
    'candidate.stepper.step3OwnWordsTitle': 'Responde con tu experiencia',
    'candidate.stepper.step3OwnWordsBody':
      'Estas respuestas las lee el equipo de reclutamiento, junto con tu CV y tu video. Cuenta un caso concreto, con tus palabras. Una respuesta genérica, o escrita por una herramienta de IA, no muestra cómo trabajas y suele quedar por debajo de quien describe algo que sí hizo.',
    'candidate.stepper.step1TipTitle': 'Consejo para destacar',
    'candidate.stepper.step1TipBody':
      'Incluye métricas concretas de impacto en tus proyectos recientes (porcentajes, ahorros, alcance).',
    // Saite Companion
    'candidate.saite.step1':
      '¡Hola! Soy Saity. Sube tu CV para empezar, ¡estaré aquí para guiarte!',
    'candidate.saite.step2': '¡Listo! Revisemos que tu información esté perfecta antes de seguir.',
    'candidate.saite.step3':
      '¡Qué buen perfil! Ahora prepárate, vamos a grabar una breve video-entrevista.',
    'candidate.saite.step4': '¡Excelente video! Ahora unas preguntas rápidas para conocerte mejor.',
    'candidate.saite.step5': '¡Casi lo tienes! Conecta tu calendario para agendar nuestra plática.',
    'candidate.saite.step6':
      '¡Lo lograste! Elige el horario que más te convenga para la entrevista.',
    'candidate.review.addWorkExp': '+ Agregar Experiencia',
    'candidate.review.noWorkExp':
      'No se detectó experiencia laboral en tu CV. Puedes agregarla manualmente.',
    'candidate.review.removeExperience': 'Eliminar esta experiencia',
    'candidate.review.remove': 'Eliminar',
    'candidate.review.addEducation': '+ Agregar Educación',
    'candidate.review.noEducation':
      'No se detectó formación académica en tu CV. Puedes agregarla manualmente.',
    'candidate.review.removeEducation': 'Eliminar esta educación',
    'candidate.review.details': 'Detalles adicionales',
    'candidate.review.add': '+ Agregar',
    'candidate.review.noSkills': 'No se detectaron habilidades. Agrégalas manualmente.',
    'candidate.review.selectCategory': 'Selecciona una categoría',
    'candidate.review.skillsPlaceholder': 'Habilidades separadas por coma',
    'candidate.review.noLanguages': 'No se detectaron idiomas. Agrégalos manualmente.',
    'candidate.review.languagePlaceholder': 'Idioma (ej: Inglés)',
    'candidate.review.levelPlaceholder': 'Nivel (ej: B2, Avanzado)',
    'candidate.review.addProject': '+ Agregar Proyecto',
    'candidate.review.noProjects':
      'No se detectaron proyectos. Si tienes proyectos relevantes, agrégalos aquí.',
    'candidate.review.removeProject': 'Eliminar este proyecto',
    'candidate.review.technologiesPlaceholder': 'React, Python, PostgreSQL...',
    'candidate.review.technologiesHint': '(separadas por coma)',
    'candidate.review.projectDescriptionPlaceholder':
      '¿En qué consistió el proyecto? ¿Cuál fue tu rol?',
    'candidate.review.addCertification': '+ Agregar Certificación',
    'candidate.review.noCertifications':
      'No se detectaron certificaciones. Si tienes certificados relevantes, agrégalos aquí.',
    'candidate.review.removeCertification': 'Eliminar esta certificación',
    'candidate.review.issueDate': 'Fecha de Emisión',
    'candidate.review.credentialId': 'ID de Credencial',
    'candidate.review.credentialPlaceholder': 'ID o código de verificación',
    'candidate.review.confirmSuccess': '✓ ¡Datos Confirmados!',
    'candidate.review.confirmButton': 'Confirmar Datos',
    'candidate.review.link': 'Enlace / URL',
    'candidate.review.certNamePlaceholder': 'Ej: AWS Certified Developer',
    'candidate.review.certIssuerPlaceholder': 'Ej: Amazon Web Services',
    'candidate.review.educationPlaceholder': 'Ej: Ingeniería en Sistemas',
    'candidate.review.institutionPlaceholder': 'Nombre de la institución',
    'candidate.review.detailsPlaceholder': 'Ej: Promedio 9.2, Titulado con Mención Honorífica',
    'candidate.review.jobTitlePlaceholder': 'Ej: Desarrollador Senior',
    'candidate.review.companyPlaceholder': 'Nombre de la empresa',
    'candidate.review.locationPlaceholder': 'Ciudad, País',
    'candidate.review.city': 'Ciudad',
    'candidate.review.state': 'Estado / Provincia',
    'candidate.review.country': 'País',
    'candidate.review.statePlaceholder': 'Estado o provincia',
    'candidate.review.skillCategory.tecnicas': 'Técnicas',
    'candidate.review.skillCategory.blandas': 'Blandas',
    'candidate.review.skillCategory.herramientas': 'Herramientas',
    'candidate.review.skillCategory.programacion': 'Lenguajes de Programación',
    'candidate.review.skillCategory.frameworks': 'Frameworks y Librerías',
    'candidate.review.skillCategory.db': 'Bases de Datos',
    'candidate.review.skillCategory.devops': 'Cloud y DevOps',
    'candidate.review.skillCategory.design': 'Diseño',
    'candidate.review.skillCategory.management': 'Gestión de Proyectos',
    'candidate.review.skillCategory.others': 'Otras',
    'candidate.review.source.referred': 'Referido / Conocido',
    'candidate.review.source.jobBoard': 'Bolsa de Trabajo (OCC, CompuTrabajo, etc.)',
    'candidate.review.source.companyWebsite': 'Sitio Web de la empresa',
    'candidate.review.source.socialNetworks': 'Redes Sociales (Facebook, Instagram, X)',
    'candidate.review.source.university': 'Universidad / Escuela',
    'candidate.review.source.other': 'Otro',
    'candidate.video.fileSizeError':
      'El archivo seleccionado pesa {{size}} MB y supera el límite de {{max}} MB. Por favor selecciona un video más pequeño.',
    'candidate.video.durationHint': 'Máximo {{max}} minutos de duración.',
    'candidate.video.durationError':
      'El video dura {{duration}} min y supera el límite de {{max}} min. Elige un video más corto.',
    'candidate.calendar.userNotAuth': 'El usuario no está autenticado o está cargando.',
    'candidate.calendar.tokenNotFound': 'Token de sesión no encontrado.',
    'candidate.questions.errorFetch': 'No se pudieron obtener las preguntas para esta vacante.',
    'candidate.questions.errorLoad': 'Error al cargar las preguntas. Por favor, intenta de nuevo.',
    'shared.profilePhotoAlt': 'Foto de perfil',
  },
  en: {
    'sidebar.panel': 'Vacancies Panel',
    'sidebar.new': 'Load Vacancy',
    'sidebar.premium': 'Premium',
    'sidebar.settings': 'Settings',
    'sidebar.backToAdmin': 'Admin panel',
    'sidebar.account': 'Account',
    'sidebar.logout': 'Log out',
    'sidebar.closing': 'Logging out...',
    'privacy.trigger': 'Privacy Notice',
    'privacy.modalTitle': 'Privacy Notice',
    'privacy.modalSubtitle': 'AI Recruitment Platform',
    'privacy.closeLabel': 'Close privacy notice',
    'privacy.section1Title': 'Data Controller',
    'privacy.section1Text':
      'AI Recruiter is the entity responsible for processing the personal data you provide during your recruitment process through this platform.',
    'privacy.section2Title': 'Data We Collect',
    'privacy.section2Intro':
      'The following personal data is collected during the application process:',
    'privacy.section2Item1': 'Full name and email address (through your Google account)',
    'privacy.section2Item2': 'Profile photo from your Google account',
    'privacy.section2Item3': 'Curriculum Vitae (CV) in PDF or DOCX format',
    'privacy.section2Item4':
      'Information extracted from your CV using Artificial Intelligence (work experience, education, skills, etc.)',
    'privacy.section2Item5': 'Personal introduction video',
    'privacy.section2Item6': 'Interview question responses',
    'privacy.section2Item7': 'Availability and Google Calendar schedule information',
    'privacy.section3Title': 'Purpose of Processing',
    'privacy.section3Intro': 'Your personal data will be used exclusively for:',
    'privacy.section3Item1':
      'Evaluating your professional profile for the position you are applying to',
    'privacy.section3Item2': 'Processing and analyzing your CV using Artificial Intelligence tools',
    'privacy.section3Item3': 'Generating a compatibility score with the job profile',
    'privacy.section3Item4': 'Facilitating communication between you and the recruiter',
    'privacy.section3Item5': 'Scheduling interviews through Google Calendar',
    'privacy.section4Title': 'Use of Artificial Intelligence',
    'privacy.section4Text':
      'This platform uses Artificial Intelligence models (Google Gemini) to analyze your CV, generate personalized interview questions, and evaluate your responses. Information processed by AI is used solely as a support tool in the selection process, and final hiring decisions are always made by people.',
    'privacy.section5Title': 'Data Transfer',
    'privacy.section5Text':
      'Your data may be shared with third-party services necessary for the platform to function: Google (authentication, calendar, and AI), Firebase (secure storage), and Stripe (recruiter payment processing). We will not sell or share your personal information with third parties for commercial purposes.',
    'privacy.section6Title': 'Data Retention',
    'privacy.section6Text':
      'Your personal data will be retained for the time necessary to complete the recruitment process for the position you applied to. You may request the deletion of your data at any time by contacting us directly.',
    'privacy.section7Title': 'Your Rights (ARCO)',
    'privacy.section7Intro': 'As the owner of your personal data, you have the right to:',
    'privacy.section7Access': 'Access:',
    'privacy.section7AccessDesc': ' Know what data we have about you',
    'privacy.section7Rectification': 'Rectification:',
    'privacy.section7RectificationDesc': ' Correct inaccurate or incomplete data',
    'privacy.section7Cancellation': 'Cancellation:',
    'privacy.section7CancellationDesc': ' Request the deletion of your data',
    'privacy.section7Opposition': 'Opposition:',
    'privacy.section7OppositionDesc': ' Object to the processing of your data',
    'privacy.section7Contact':
      'To exercise these rights, write to recruiter@insaite.io, or to the contact address provided by the company responsible for the recruitment process.',
    'privacy.section8Title': 'Security',
    'privacy.section8Text':
      'Your personal data is stored securely using Google Firebase, with encryption in transit and at rest. We implement administrative, technical, and physical security measures to protect your information against unauthorized access, loss, or alteration.',
    'privacy.section9Title': 'Consent',
    'privacy.section9Text':
      'By continuing with the application process and uploading your CV, video, or answering the questions, you accept the terms of this privacy notice and consent to the processing of your personal data as described herein.',
    // Section 10 — required by Google verification for sensitive scopes.
    // Without the Limited Use disclosure the request is rejected.
    // ── Terms of service and public legal pages ──────────────────────
    'legal.backHome': 'Back to home',
    'legal.privacyLink': 'Privacy notice',
    'legal.termsLink': 'Terms of service',
    'legal.contact': 'Contact',

    'terms.title': 'Terms of Service',
    'terms.subtitle': 'AI Recruitment Platform',
    'terms.section1Title': 'What this platform is',
    'terms.section1Text':
      'AI Recruiter is a recruitment platform that helps companies publish job openings and evaluate applications with the support of artificial intelligence. Candidates upload their CV, record a video introduction, answer questions, and schedule their interview.',
    'terms.section2Title': 'Who can use it',
    'terms.section2Text':
      "You must be of legal age and have a Google or Microsoft account to sign in. Each instance of the platform is operated by the company running the selection process, which is the controller of its candidates' data.",
    'terms.section3Title': 'Acceptable use',
    'terms.section3Intro': 'By using the platform you agree to:',
    'terms.section3Item1': 'Provide truthful information about your experience and education.',
    'terms.section3Item2': 'Not impersonate another person.',
    'terms.section3Item3':
      'Not upload illegal or offensive content, or content that infringes third-party rights.',
    'terms.section3Item4': 'Not attempt to access applications or data that are not yours.',
    'terms.section4Title': 'Automated decisions',
    'terms.section4Text':
      'Artificial intelligence generates a summary and an indicative score for each application. That score is an aid for the recruitment team, not a decision: the final assessment is made by people. You may request human review from the company running the process.',
    'terms.section5Title': 'Your content',
    'terms.section5Text':
      'You retain ownership of your CV, your video, and your answers. You grant us only the permission needed to process them and show them to the company whose opening you applied to, for the duration of the selection process.',
    'terms.section6Title': 'Service availability',
    'terms.section6Text':
      'The platform is provided as is, without a guarantee of uninterrupted availability. There may be maintenance windows or outages. We are not responsible for the hiring decision, which rests entirely with the company publishing the opening.',
    'terms.section7Title': 'Privacy',
    'terms.section7Text':
      'The processing of your personal data is described in the Privacy Notice, which forms part of these terms.',
    'terms.section8Title': 'Changes',
    'terms.section8Text':
      'These terms may be updated. The date of the last revision appears at the foot of the page. Continued use of the platform after a change constitutes acceptance.',
    'terms.lastUpdate': 'Last updated: February 2026',

    'landing.tagline': 'AI-assisted recruitment, from the job opening to the interview.',
    'landing.whatTitle': 'What it does',
    'landing.whatText':
      'AI Recruiter supports a full selection process: the company publishes an opening from its job description, the platform generates the interview questions, and each candidate uploads their CV, records an introduction, answers, and schedules their interview. The recruitment team sees applications ranked with a summary and an indicative score.',
    'landing.calendarTitle': 'Google Calendar integration',
    'landing.calendarText':
      'If you connect your calendar, the platform reads your busy time blocks so it can offer only slots where both you and the interviewer are free, and creates the interview event you confirm. It does not read the contents of your events, your email, or your contacts, and you can revoke access at any time.',
    'landing.enter': 'Enter the platform',

    'privacy.section10Title': 'Use of your Google account data',
    'privacy.section10Intro':
      'If you connect your calendar, the platform requests Google Calendar permissions and uses them only for the following:',
    'privacy.section10Item1':
      'Read your busy time blocks, so it can offer only slots where both you and the interviewer are free.',
    'privacy.section10Item2': 'Create the interview event you confirm in your calendar.',
    'privacy.section10Item3':
      'We do not read the contents of your events, your email, or your contacts.',
    'privacy.section10Item4':
      'We do not sell, transfer, or share this data with third parties, nor use it for advertising or to train artificial intelligence models.',
    'privacy.section10LimitedUse':
      "This application's use of information received from Google APIs adheres to the Google API Services User Data Policy, including its Limited Use requirements.",
    'privacy.section10Revoke':
      'You can revoke this access at any time, without going through us, at myaccount.google.com/permissions. Once revoked we can no longer read your availability; events already created remain in your calendar.',
    'privacy.lastUpdate': 'Last updated: February 2026',
    'settings.tab.profile': 'Profile',
    'settings.tab.notifications': 'Notifications',
    'settings.tab.privacy': 'Privacy & legal',
    'settings.tab.theme': 'Theme',
    'settings.tab.language': 'Language',
    'settings.tab.administration': 'Administration',
    'settings.loading': 'Loading settings...',
    'settings.profile.noName': 'No name',
    'settings.profile.noEmail': 'No email',
    'settings.notifications.soon': 'Email notification options (coming soon).',
    'settings.privacy.soon': 'Manage your privacy and data (coming soon).',
    'settings.legal.title': 'Privacy & legal',
    'settings.legal.privacyTitle': 'Privacy notice',
    'settings.legal.privacyDesc': 'How we handle your personal data.',
    'settings.legal.privacyCta': 'Read notice',
    'settings.legal.termsTitle': 'Terms of service',
    'settings.legal.termsDesc': 'Platform usage rules.',
    'settings.legal.termsCta': 'Read terms',
    'settings.theme.title': 'Appearance',
    'settings.theme.default': 'Default (blue)',
    'settings.theme.violet': 'Violet',
    'settings.theme.lightPure': 'Pure white',
    'settings.theme.violetInverted': 'Violet inverted',
    'settings.theme.black': 'Pure Black',
    'settings.language.title': 'Language',
    'settings.language.es': 'Spanish',
    'settings.language.en': 'English',
    'title.panel': 'Vacancies Panel',
    'title.new': 'Vacancy Upload',
    'title.checkout': 'Premium',
    'title.settings': 'Settings',
    'title.edit': 'Edit vacancy',
    'title.workspace': 'Workspace',
    'title.candidatesList': 'Candidates list',
    'title.questions': 'Vacancy questions',
    'title.metrics': 'Metrics',
    'nav.backToEdit': 'Back to edit vacancy',
    'nav.backToPanel': 'Back to panel',
    'dashboard.intro':
      'Manage your vacancies. Filter, change status, and access workspace or questions from here.',
    'dashboard.pageTitle': 'Vacancy management',
    'dashboard.breadcrumb': 'Admin panel · Vacancy management',
    'dashboard.activeVacanciesBadge': '{{count}} active vacancies',
    'dashboard.kpiTotalVacancies': 'Total vacancies',
    'dashboard.kpiActiveApplications': 'Active applications',
    'dashboard.kpiTrendMonth': '{{sign}}{{count}} this month',
    'dashboard.kpiAvgApplicants': 'Average {{avg}} candidates / vacancy',
    'dashboard.kpiOpenLabel': '{{count}} open',
    'dashboard.applicantsColumn': 'Applicants',
    'dashboard.applicantsCount': '{{count}} applicants',
    'dashboard.totalBadge': '{count} vacancies total',
    'dashboard.paginationShowing': 'Showing {from} - {to} of {total} vacancies',
    'dashboard.createVacancy': '+ New vacancy',
    'dashboard.loading': 'Loading vacancies...',
    'dashboard.retry': 'Retry',
    'dashboard.noResultsHint': 'No vacancies match the current filters.',
    'dashboard.titleColumn': 'Title',
    'dashboard.filterSearch': 'Search by title...',
    'dashboard.statusColumn': 'Status',
    'dashboard.filterAll': 'All',
    'dashboard.createdAt': 'Created',
    'dashboard.filterAllDates': 'No order',
    'dashboard.filterDateOldest': 'Oldest first',
    'dashboard.filterDateNewest': 'Newest first',
    'dashboard.updatedAt': 'Last updated',
    'dashboard.actions': 'Actions',
    'dashboard.errorUpdate': 'Could not update',
    'dashboard.deleteSuccess': 'Vacancy "{{title}}" deleted successfully.',
    'dashboard.errorDelete': 'Could not delete vacancy. Please try again.',
    'dashboard.tooltipWorkspace': 'View candidates for this vacancy',
    'dashboard.tooltipMetrics': 'View vacancy metrics',
    'dashboard.tooltipCopyUrl': 'Copy public URL',
    'dashboard.linkCopied': 'Link copied',
    'dashboard.tooltipDelete': 'Delete vacancy',
    'dashboard.tooltipFichaPreview': 'Candidate-facing posting preview',
    'vacancy.fichaPreview.backToPanel': 'Back to dashboard',
    'vacancy.fichaPreview.langAria': 'Posting language',
    'vacancy.fichaPreview.downloadPdf': 'Download PDF',
    'vacancy.fichaPreview.loading': 'Loading preview…',
    'vacancy.fichaPreview.loadError': 'Could not load preview.',
    'vacancy.fichaPreview.downloadError': 'Could not get PDF.',
    'vacancy.fichaPreview.iframeTitle': 'Vacancy posting preview',
    'editor.fichaUpdatedToast': 'Candidate posting updated. Preview it from the vacancy dashboard.',
    'editor.requiredDoc.title': 'Document for candidates',
    'editor.requiredDoc.hint':
      'Extra attachment (certification, template) separate from the published posting PDF.',
    'editor.requiredDoc.masterToggle': 'Request a document from candidates',
    'editor.requiredDoc.assetToggle': 'Provide a file for download',
    'editor.requiredDoc.missingFile': 'File missing',
    'editor.requiredDoc.uploadFile': 'Upload file',
    'editor.requiredDoc.replaceFile': 'Replace file',
    'editor.requiredDoc.removeFile': 'Remove file',
    'editor.requiredDoc.modalTitle': 'Upload required document',
    'editor.requiredDoc.modalHint': 'PDF or DOCX, up to {{mb}} MB.',
    'editor.requiredDoc.chooseFile': 'Choose file',
    'editor.requiredDoc.uploading': 'Uploading…',
    'editor.requiredDoc.invalidType': 'PDF or DOCX only.',
    'editor.requiredDoc.tooLarge': 'File exceeds {{mb}} MB.',
    'editor.requiredDoc.uploadUrlError': 'Could not prepare upload.',
    'editor.requiredDoc.uploadError': 'Upload failed.',
    'editor.requiredDoc.saveError': 'Could not save settings.',
    'editor.requiredDoc.candidateUploadToggle': 'Ask candidate for a document (deliverable)',
    'editor.requiredDoc.candidateRequiredToggle': 'Required to continue',
    'editor.requiredDoc.candidateLabel': 'Document name',
    'candidate.requiredDoc.defaultLabel': 'Requested document',
    'candidate.requiredDoc.upload': 'Upload document',
    'candidate.requiredDoc.replace': 'Replace document',
    'candidate.requiredDoc.uploading': 'Uploading…',
    'candidate.requiredDoc.missingHint': 'Upload this document to continue.',
    'candidate.requiredDoc.invalidType': 'File type not allowed.',
    'candidate.requiredDoc.tooLarge': 'File exceeds {{mb}} MB.',
    'candidate.requiredDoc.uploadFailed': 'Could not upload document.',
    'candidate.requiredDoc.downloadInstructivo': 'Download recruiter instructions',
    'candidate.application.checklistRequiredDoc': 'Requested document',
    'candidate.application.updateRequiredDoc': 'Update document',
    'notification.recruiter.requiredDocUploaded.title': 'New candidate document',
    'notification.recruiter.requiredDocUploaded.message':
      'A candidate uploaded the requested document. See Deliverables.',
    'notification.recruiter.requiredDocUpdated.title': 'Candidate document updated',
    'notification.recruiter.requiredDocUpdated.message':
      'The candidate replaced the requested document.',
    'workspace.detailTab.deliverables': 'Deliverables',
    'workspace.detailNoDeliverables': 'This candidate has not uploaded deliverables yet.',
    'workspace.detailDeliverableFile': 'Candidate document',
    'workspace.downloadRequiredDoc': 'Download document',
    'publicVacante.downloadFicha': 'Download job posting sheet',
    'publicVacante.gettingFicha': 'Getting posting sheet…',
    'dashboard.tooltipQuestions': 'Edit interview questions',
    'dashboard.tooltipEdit': 'Edit vacancy',
    'dashboard.tooltipStatus': 'Change vacancy status',
    'dashboard.tooltipFilterSearch': 'Search by job title',
    'dashboard.tooltipFilterStatus': 'Filter by status',
    'dashboard.tooltipFilterDate': 'Sort by date',
    'dashboard.noTitle': 'No Title',
    // ──── Admin ────
    'admin.redirecting': 'Redirecting...',
    'admin.account': 'Account',
    'admin.loading': 'Loading...',
    'admin.dashboard': 'Dashboard',
    'admin.users': 'Users',
    'admin.vacancies': 'Vacancies',
    'admin.config': 'Configuration',
    'admin.audit': 'Audit',
    'admin.aiUsage': 'AI Usage',
    'admin.notifications': 'Notifications',
    'admin.markAllRead': 'Mark all as read',
    'admin.noNotifications': 'No notifications',
    'admin.breadcrumb': 'Administration Panel',
    'admin.sidebar.expand': 'Expand menu',
    'admin.sidebar.collapse': 'Collapse menu',
    'admin.sidebar.panelAdmin': 'Administrator panel',
    'admin.sidebar.panelRecruiter': 'Recruiter panel',
    'admin.logout': 'Log out',
    'admin.dashboard.title': 'Administrative Dashboard',
    'admin.dashboard.breadcrumbOverview': 'Overview',
    'admin.dashboard.cardCandidates': 'Candidates',
    'admin.dashboard.cardVacancies': 'Vacancies',
    'admin.dashboard.cardTeam': 'Team',
    'admin.dashboard.cardRecruiters': 'Recruiters',
    'admin.dashboard.cardApplications': 'Applications',
    'admin.dashboard.cardAiUsage': 'AI usage',
    'admin.dashboard.aiTokensDetail': 'Input: {{input}} · Output: {{output}} tokens',
    'admin.dashboard.aiCostDelta': '{{pct}}% vs previous month',
    'admin.dashboard.kpiDeltaWeek': '{{pct}}% vs previous week',
    'admin.dashboard.vacancyMixTitle': 'Active vs closed vacancies',
    'admin.dashboard.vacanciesActive': 'Active',
    'admin.dashboard.vacanciesInactive': 'Closed / inactive',
    'admin.dashboard.vacanciesActiveTotal': '{{active}} active · {{total}} total',
    'admin.dashboard.suspendedAlert':
      'Attention! There are {{count}} suspended users requiring review.',
    'admin.dashboard.kpisSection': 'Key indicators',
    'admin.dashboard.tabsLabel': 'Dashboard sections',
    'admin.dashboard.tabVisual': 'Visual Analysis & Charts',
    'admin.dashboard.tabTrends': 'Weekly Trends',
    'admin.dashboard.barChartTitle': 'Application volume by cycle',
    'admin.dashboard.chartsEmpty': 'No trend data available to render charts for this period.',
    'admin.dashboard.trendsEmpty': 'No weekly trend rows available.',
    'admin.dashboard.chartsSection': 'Visual analysis',
    'admin.dashboard.chartsPlaceholder': 'Coming soon',
    'admin.dashboard.weeklyTrends': 'Weekly Trends',
    'admin.dashboard.insightsTitle': 'AI Insights',
    'admin.dashboard.insightsLoading': 'Generating recommendations from your metrics…',
    'admin.dashboard.insightsRefreshAria': 'Refresh insight',
    'admin.dashboard.insightsUpdatedAt': 'Updated on {{date}}',
    'admin.dashboard.insightsEmptyLine1': 'This is your first insight.',
    'admin.dashboard.insightsEmptyLine2Prefix':
      'Once you have active vacancies or applications in the period we analyze, press',
    'admin.dashboard.insightsEmptyLine2Suffix':
      'to get the summary. It is a one-time action; it does not regenerate when you enter.',
    'admin.dashboard.insightsError':
      'We could not generate the insight right now. The rest of the dashboard is still available.',
    'admin.dashboard.showAllWeeks': 'Show all',
    'admin.dashboard.colWeek': 'Week',
    'admin.dashboard.colCandidates': 'Candidates',
    'admin.dashboard.colVacancies': 'Vacancies',
    'admin.dashboard.colApplications': 'Applications',
    'notification.newApplication.title': 'New application completed',
    'notification.newApplication.message':
      'A candidate completed the application process for a vacancy.',
    'notification.candidate.selected.title': 'You were selected!',
    'notification.candidate.selected.message':
      'The company selected you for this role. Check your application details.',
    'notification.candidate.discarded.title': 'Application update',
    'notification.candidate.discarded.message':
      'Your application will not move forward for this role. You can apply to other jobs.',
    'notification.candidate.vacancyClosed.title': 'Vacancy closed',
    'notification.candidate.vacancyClosed.message':
      'The position has been filled or the vacancy was closed.',
    'admin.users.title': 'User Management',
    'admin.users.totalBadge': '{count} users total',
    'admin.users.export': 'Export CSV',
    'admin.users.inviteButton': 'New member',
    'admin.users.inviteModalTitle': 'New member',
    'admin.users.inviteEmailLabel': 'Email address',
    'admin.users.inviteEmailPlaceholder': 'name@company.com',
    'admin.users.inviteRoleLabel': 'Role',
    'admin.users.inviteSubmit': 'Invite',
    'admin.users.inviteSuccess': 'Invitation recorded for {email}',
    'admin.users.inviteEmailInvalid': 'Enter a valid email address',
    'admin.users.inviteErrorUserExists': 'That email already has an account',
    'admin.users.inviteErrorInviteExists': 'A pending invitation already exists for that email',
    'admin.users.filterSearchPlaceholder': 'Search email or name...',
    'admin.users.filterAllLastLogin': 'Last login',
    'admin.users.lastLogin7': 'Last 7 days',
    'admin.users.lastLogin30': 'Last 30 days',
    'admin.users.lastLogin90': 'Last 90 days',
    'admin.users.clear': 'Clear',
    'admin.users.empty': 'No users found',
    'admin.users.columnUser': 'User',
    'admin.users.columnRoleAssigned': 'Assigned role',
    'admin.users.superAdminBadge': 'Super Admin',
    'admin.users.paginationShowing': 'Showing {from} - {to} of {total} users',
    'admin.users.paginationPrev': 'Previous',
    'admin.users.paginationNext': 'Next',
    'admin.users.rowsPerPage': 'Rows per page:',
    'admin.users.auditFooter':
      'Audit log active: All role changes and impersonation events are monitored for security.',
    'admin.users.filterEmail': 'Email',
    'admin.users.filterUid': 'UID',
    'admin.users.filterAllRoles': 'All roles',
    'admin.users.roleCandidate': 'Candidate',
    'admin.users.roleRecruiter': 'Recruiter',
    'admin.users.roleAdmin': 'Admin',
    'admin.users.filterAllStatuses': 'All statuses',
    'admin.users.statusActive': 'Active',
    'admin.users.statusSuspended': 'Suspended',
    'admin.users.search': 'Search',
    'admin.users.columnRole': 'Role',
    'admin.users.columnEmail': 'Email',
    'admin.users.columnName': 'Name',
    'admin.users.columnStatus': 'Status',
    'admin.users.columnLastLogin': 'Last login',
    'admin.users.columnActions': 'Actions',
    'admin.users.activate': 'Activate',
    'admin.users.suspend': 'Suspend',
    'admin.users.impersonate': 'Impersonate',
    'admin.users.impersonateTitle': 'Log in as this user',
    'admin.users.changeRole': 'Change role',
    'admin.users.rolePending': 'Pending assignment',
    'admin.users.ownerTitle':
      'Instance owner. The only one who can appoint or remove administrators. Transfer ownership before changing their role.',
    'admin.users.transfer': 'Make owner',
    'admin.users.transferTitle': 'Transfer instance ownership to this person',
    'admin.users.transferConfirm':
      'Transfer instance ownership to {email}? You will stop being the owner, but remain an administrator.',
    'pending.title': 'Your account is pending approval',
    'pending.lead': 'You signed in successfully, but no role has been assigned to you yet.',
    'pending.hint':
      'Ask an administrator to assign you a role. Once they do, sign in again for it to take effect.',
    'pending.signOut': 'Sign out',
    'maintenance.title': 'We are under maintenance',
    'maintenance.defaultMessage': 'The platform is under maintenance. Please try again later.',
    'maintenance.retry': 'Retry',
    'maintenance.signOut': 'Sign out',
    'maintenance.adminAccess': 'Administrator access',
    'maintenance.adminBanner': 'Maintenance mode active — administrator access only',
    'maintenance.checking': 'Checking status...',
    'hiringPause.bannerTitle': 'Hiring temporarily paused',
    'hiringPause.bannerDismiss': 'Got it',
    'hiringPause.defaultMessage':
      'New vacancies and applications are temporarily unavailable. Please try again later.',
    'hiringPause.recruiterHint':
      'You can review existing candidates; creating or activating vacancies is disabled.',
    'hiringPause.candidateHint':
      'Applications are paused. Your on-screen progress is saved, but you cannot submit CV, video, answers, or schedule until hiring resumes.',
    'hiringPause.calendarHint':
      'You can connect your calendar; scheduling will be available when hiring resumes.',
    'hiringPause.blocked.uploadCv': 'Applications paused',
    'hiringPause.blocked.uploadVideo': 'Applications paused',
    'hiringPause.blocked.confirmReview': 'Applications paused',
    'hiringPause.blocked.submitAnswers': 'Applications paused',
    'hiringPause.blocked.scheduleInterview': 'Applications paused',
    'hiringPause.blocked.createVacancy': 'Vacancy upload paused',
    'hiringPause.blocked.activateVacancy': 'Vacancy activation paused',
    'hiringPause.blocked.saveActiveVacancy': 'Active vacancy save paused',
    'hiringPause.modalTitle': 'Action unavailable',
    'hiringPause.modalConfirm': 'Got it',
    'hiringPause.modal.seeBanner': 'See notice at the top',
    'hiringPause.modal.upload_cv.title': 'We could not upload your CV',
    'hiringPause.modal.upload_cv.body': 'Applications are temporarily paused.',
    'hiringPause.modal.confirm_cv_review.title': 'We could not save your review',
    'hiringPause.modal.confirm_cv_review.body': 'Applications are temporarily paused.',
    'hiringPause.modal.upload_video.title': 'We could not upload your video',
    'hiringPause.modal.upload_video.body': 'Applications are temporarily paused.',
    'hiringPause.modal.submit_answers.title': 'We could not submit your answers',
    'hiringPause.modal.submit_answers.body': 'Applications are temporarily paused.',
    'hiringPause.modal.schedule_interview.title': 'We could not schedule your interview',
    'hiringPause.modal.schedule_interview.body': 'Applications are temporarily paused.',
    'hiringPause.modal.create_vacancy.title': 'We could not create the vacancy',
    'hiringPause.modal.create_vacancy.body': 'Hiring is paused on the platform.',
    'hiringPause.modal.activate_vacancy.title': 'We could not activate the vacancy',
    'hiringPause.modal.activate_vacancy.body': 'Hiring is paused on the platform.',
    'hiringPause.modal.save_active_vacancy.title': 'We could not save the vacancy',
    'hiringPause.modal.save_active_vacancy.body': 'Hiring is paused on the platform.',
    'hiringPause.emptyStateTitle': 'Applications paused',
    'admin.users.roleLockedTitle':
      'Admin via the deployment ADMIN_EMAILS variable. Edit that variable to change it.',
    'admin.users.roleAdminReadOnlyTitle':
      'Administrator. Only the instance owner can change this role.',
    'admin.users.roleReadOnlyTitle': 'This role cannot be changed from this panel.',
    'admin.impersonation.label': 'Viewing as user: {{uid}}',
    'admin.impersonation.exit': 'Exit',
    'admin.config.title': 'System Configuration',
    'admin.config.tabGeneral': 'General',
    'admin.config.tabNotifications': 'Notifications',
    'admin.config.platformStatus': 'Platform status',
    'admin.config.operationalLimits': 'Operational limits',
    'admin.config.maintenanceMode': 'Maintenance mode',
    'admin.config.maintenanceMessage': 'Maintenance message',
    'admin.config.hiringPause': 'Global hiring pause',
    'admin.config.hiringPauseMessage': 'Hiring pause message',
    'admin.config.confirmMaintenance':
      'Enable maintenance mode? Only administrators will be able to sign in.',
    'admin.config.confirmHiringPause':
      'Pause hiring globally? Vacancies and applications will be blocked.',
    'admin.config.confirmMaintenanceTitle': 'Enable maintenance mode',
    'admin.config.confirmHiringPauseTitle': 'Pause hiring globally',
    'admin.config.previewSection': 'User-facing preview',
    'admin.config.previewTabCandidate': 'Candidate view',
    'admin.config.previewTabRecruiter': 'Recruiter view',
    'admin.config.confirmAction': 'Confirm',
    'admin.config.limitsFree': 'Limits (free plan)',
    'admin.config.limitVacancies': 'Max vacancies per recruiter (free)',
    'admin.config.generalKeyValue': 'General (key-value)',
    'admin.config.noKeys': 'No other keys. Add one below if needed.',
    'admin.config.newKeyPlaceholder': 'New key',
    'admin.config.newValuePlaceholder': 'Value (text or JSON)',
    'admin.config.add': 'Add',
    'admin.config.saving': 'Saving...',
    'admin.config.save': 'Save',
    'admin.config.savedMsg': 'Configuration saved.',
    'admin.config.notifPlatformTitle': 'Platform Notifications',
    'admin.config.notifDescription':
      'Summary of notifications (new applications, system notices). The header bell shows the summary; here you can see the full detail.',
    'admin.config.deleteAll': 'Delete all',
    'admin.config.deleteConfirm': 'Delete all notifications?',
    'admin.config.markRead': 'Mark read',
    'admin.config.labelVacancy': 'Vacancy',
    'admin.config.labelIdVacancy': 'Vacancy ID',
    'admin.config.labelCandidate': 'Candidate',
    'admin.config.labelEmail': 'Email',
    'admin.config.labelIdApp': 'App ID',
    'admin.config.goToPanel': 'Go to panel',
    'admin.audit.title': 'Audit Log',
    'admin.audit.filterAdminEmail': 'Admin (email)',
    'admin.audit.filterAllActions': 'All actions',
    'admin.audit.filterFrom': 'From',
    'admin.audit.filterTo': 'To',
    'admin.audit.search': 'Search',
    'admin.audit.exportCsv': 'Export CSV',
    'admin.audit.colDate': 'Date',
    'admin.audit.colAdmin': 'Admin',
    'admin.audit.colAction': 'Action',
    'admin.audit.colTarget': 'Target',
    'admin.audit.colIp': 'IP',
    'admin.audit.prev': 'Previous',
    'admin.audit.next': 'Next',
    'admin.audit.offsetLabel': 'Offset: {offset}',
    'admin.audit.totalBadge': '{count} events total',
    'admin.audit.paginationShowing': 'Showing {from} - {to} of {total} events',
    'admin.audit.empty': 'No audit events found.',
    'admin.aiMonitoring.title': 'AI Monitoring',
    'admin.aiMonitoring.breadcrumb': 'AI Monitoring',
    'admin.aiMonitoring.pageTitle': 'AI Monitoring & Token Usage',
    'admin.aiMonitoring.callsRegistered': '{{count}} calls logged',
    'admin.aiMonitoring.kpisSection': 'AI usage indicators',
    'admin.aiMonitoring.kpiTokens': 'Total tokens (30D)',
    'admin.aiMonitoring.kpiTokensBreakdown': 'Input {{input}} · Output {{output}}',
    'admin.aiMonitoring.kpiCost': 'Accumulated cost',
    'admin.aiMonitoring.kpiCostHint': 'Estimated ATS copilot compute cost',
    'admin.aiMonitoring.kpiSuccess': 'Successful calls',
    'admin.aiMonitoring.kpiSuccessMeta': '{{ok}} / {{total}} executions',
    'admin.aiMonitoring.filterCaller': 'Caller or ID',
    'admin.aiMonitoring.filterModel': 'LLM model',
    'admin.aiMonitoring.filterFrom': 'From',
    'admin.aiMonitoring.filterTo': 'To',
    'admin.aiMonitoring.filterApply': 'Filter',
    'admin.aiMonitoring.tableSection': 'Execution log',
    'admin.aiMonitoring.colCost': 'Estimated cost',
    'admin.aiMonitoring.colStatus': 'Status',
    'admin.aiMonitoring.empty': 'No calls match these filters.',
    'admin.aiMonitoring.summary':
      'Log of calls to Gemini (tokens per call). This page: {{total}} tokens.',
    'admin.aiMonitoring.colDate': 'Date & time',
    'admin.aiMonitoring.colModel': 'LLM model',
    'admin.aiMonitoring.colCaller': 'Service (caller)',
    'admin.aiMonitoring.colInput': 'Input',
    'admin.aiMonitoring.colOutput': 'Output',
    'admin.aiMonitoring.colTotal': 'Total tokens',
    'admin.aiMonitoring.colError': 'Error',
    'admin.aiMonitoring.paginationShowing': 'Showing {from} - {to} of {total} calls',
    'editor.hintModality': 'On-site, hybrid, or remote',
    'editor.hintSalary': 'Salary range (optional)',
    'editor.hintSalaryConfidential': 'If enabled, candidates will not see the amount',
    'editor.hintExperience': 'Minimum years of experience',
    'editor.hintLocation': 'City or work location',
    'editor.hintSkillsHard': 'One per line, e.g. JavaScript, Python',
    'editor.hintSkillsSoft': 'One per line, e.g. Teamwork',
    'common.errorUnknown': 'Unknown error',
    'common.confirmDelete': 'Confirm deletion',
    'modal.cancel': 'Cancel',
    'modal.cannotUndo': 'This action cannot be undone.',
    'modal.delete': 'Delete',
    'modal.deleteVacancyConfirm': 'Delete vacancy "{{title}}"?',
    'modal.deleteQuestionConfirm': 'Delete this question?',
    'modal.savingChanges': 'Saving changes...',
    'modal.savedSuccess': 'Changes saved successfully.',
    'modal.logoutTitle': 'Log out',
    'modal.logoutMessage': 'Are you sure you want to log out?',
    'modal.logoutConfirm': 'Yes, log out',
    'modal.vacancySavedSuccess': 'Vacancy saved successfully',
    'modal.questionsSavedSuccess': 'Questions saved successfully',
    'modal.done': 'Done',
    'editor.statusDraft': 'Draft',
    'editor.statusActive': 'Active',
    'editor.statusPaused': 'Paused',
    'editor.statusClosed': 'Closed',
    'questions.deleteSuccess': 'Question deleted successfully.',
    'questions.deleteError': 'Error deleting question. Please try again.',
    'questions.saving': 'Saving questions...',
    'questions.saveEdit': 'Save changes',
    'questions.cancelEdit': 'Cancel',
    'questions.editQuestion': 'Edit this question',
    'questions.deleteQuestion': 'Delete this question',
    'questions.saveQuestions': 'Save Questions',
    'questions.saveChanges': 'Save Changes',
    'questions.confirmDelete': 'Confirm Deletion',
    'questions.deleteConfirmMessage': 'Are you sure you want to delete this question?',
    'questions.deleting': 'Deleting...',
    'questions.generateAnother': 'Generate another question',
    'workspace.intro': 'Review candidates who applied to this vacancy and their match scores.',
    'workspace.compatibilityBreakdown': 'Compatibility breakdown',
    'workspace.candidateAnswers': 'Candidate answers',
    'workspace.noCandidates': 'No candidates yet',
    'workspace.noCandidatesDesc': 'Candidates will appear here when they apply to your vacancy.',
    'workspace.emptyHint': 'Share the vacancy link to receive applications.',
    'workspace.deleteSuccess': 'Application removed.',
    'workspace.deleteError': 'Error removing application.',
    'workspace.deleteConfirmMessage': "Remove {{name}}'s application? This cannot be undone.",
    'workspace.confirmDelete': 'Confirm deletion',
    'workspace.tooltipDelete': 'Remove application',
    'workspace.deleteWarning':
      'This action cannot be undone and will remove all data associated with this candidate for this vacancy.',
    'common.errorLoad': 'Could not load. Check your connection and try again.',
    'common.errorSave': 'Could not save. Check your connection and try again.',
    'vacancy.saved': 'Vacancy saved successfully',
    'editor.statusLabel': 'Status',
    'editor.savingDraft': 'Saving draft...',
    'editor.publishing': 'Publishing vacancy...',
    'editor.pausing': 'Pausing vacancy...',
    'editor.closing': 'Closing vacancy...',
    'editor.saving': 'Saving...',
    'editor.saveChanges': 'Save Changes',
    'editor.continueToQuestions': 'Continue to questions',
    'editor.addHardSkill': 'Add technical skill',
    'editor.addSoftSkill': 'Add soft skill',
    'editor.skillsDetectedCount': '{{count}} detected',
    'editor.cardMainTitle': 'Main vacancy data',
    'editor.cardCompensationTitle': 'Conditions and compensation',
    'editor.successDraft': 'Draft saved',
    'editor.successPublished': 'Vacancy published',
    'editor.successPaused': 'Vacancy paused',
    'editor.successClosed': 'Vacancy closed',
    'editor.loadSuccess': 'Data loaded correctly. You can now edit the vacancy.',
    'editor.copyUrlPrompt': 'Copy the URL:',
    'editor.placeholderSoftSkills': 'E.g., Communication\nLeadership',
    'editor.errorSaveHint': 'Error saving.',
    'editor.positionTitle': 'Position title',
    'editor.vacancySummary': 'Vacancy summary',
    'editor.intro':
      'Review and complete the data. Choose the status (Draft, Active, Paused or Closed) and save. Then you can configure the interview questions.',
    'editor.hintFirstSteps':
      'Start by uploading the job description in "Load vacancy" or create one from the dashboard.',
    'editor.softSkills': 'Soft skills',
    'editor.location': 'Location',
    'editor.addLocation': 'Add',
    'editor.locationCitiesCount': '{{count}} cities added',
    'editor.errorLocationMaxLength':
      'Location cannot exceed 120 characters. Remove cities or shorten the names.',
    'editor.modality': 'Modality',
    'editor.modalityPresencial': 'On-site',
    'editor.modalityRemoto': 'Remote',
    'editor.modalityHibrido': 'Hybrid',
    'editor.salaryMin': 'Minimum Salary',
    'editor.salaryMax': 'Maximum Salary',
    'editor.currency': 'Currency',
    'editor.salaryConfidential': 'Show salary as "Confidential" to candidate',
    'editor.experienceYears': 'Required Experience (Years)',
    'editor.hardSkills': 'Hard Skills',
    'editor.placeholderTitle': 'E.g. Senior Full Stack Developer',
    'editor.placeholderSummary': 'Briefly describe the vacancy...',
    'editor.placeholderLocation': 'E.g. Mexico City, CDMX',
    'editor.placeholderSalaryMin': 'E.g. 30000',
    'editor.placeholderSalaryMax': 'E.g. 45000',
    'editor.placeholderSkills': 'One per line. E.g. Python, SQL, AWS',
    'editor.noTitle': 'No Title',
    'editor.waitingAnalysis': 'Waiting for AI analysis results and backend connection.',
    'editor.copyUrl': 'Copy URL',
    'editor.copied': 'Copied!',
    'editor.copyUrlDraft':
      'Publish the vacancy before sharing the link: while it is a draft, anyone who opens it will see it is unavailable.',
    'editor.errorNoId': 'Error: No vacancy ID provided',
    'editor.loadingVacancy': 'Loading vacancy data...',
    'editor.errorLoadHint': 'Error loading:',
    'questions.generatedCount': 'Generated Questions ({{count}})',
    'questions.difficulty': 'Difficulty',
    'questions.loading': 'Loading questions...',
    'questions.questionType': 'Question type:',
    'questions.typeLabel': 'Type:',
    'questions.typeTechnical': 'Technical',
    'questions.typeBehavioral': 'Behavioral',
    'questions.typeCulture': 'Culture',
    'questions.difficultyEasy': 'Easy',
    'questions.difficultyMedium': 'Medium',
    'questions.difficultyHard': 'Hard',
    'questions.placeholderQuestion': 'Write the question...',
    'questions.noQuestionsYet':
      'No questions generated yet. Questions are generated automatically when you save the vacancy. If you just saved, wait a few seconds and refresh the page.',
    'questions.regenerating': 'Regenerating questions...',
    'questions.regenerateSuccess':
      '✅ New questions generated. Please review and approve if you like them.',
    'questions.regenerateError': 'Error regenerating questions.',
    'questions.addQuestionError': 'Error generating new question.',
    'questions.deleteErrorShort': 'Error deleting question.',
    'questions.savingChanges': 'Saving changes...',
    'questions.updateError': 'Error updating question.',
    'questions.loadError': 'Error loading questions. Please try again.',
    'editor.autoSaved': 'Changes saved',
    'draft.saving': 'Saving…',
    'draft.savedAgo': 'Saved · {{minutes}} min ago',
    'draft.offline': 'Offline — saved locally',
    'draft.blockedPublished': "This vacancy is already active, changes aren't saved as draft",
    'draft.blockedSubmitted': 'You already submitted your answers; drafts are no longer saved',
    'draft.discardDraft': 'Discard draft and restore last saved version',
    'draft.discardConfirmTitle': 'Discard draft',
    'draft.discardConfirmMessage':
      'Autosaved changes will be removed and official saved data will be restored. Continue?',
    'draft.discardConfirm': 'Confirm',
    'dashboard.draftEditedAgo': 'Edited {{minutes}} min ago',
    'editor.dataLoadedBetter':
      'We have extracted the data from the document. Review and correct as needed before publishing.',
    'login.welcome': 'Welcome',
    'login.subtitle': 'AI Recruitment Platform',
    'login.continueGoogle': 'Continue with Google',
    'login.connecting': 'Connecting...',
    'login.accessingPanel': 'Accessing Panel...',
    'login.errorServer': 'Error processing server response.',
    'login.footer': '© AI Recruiter',
    'upload.title': 'Let AI build your vacancy',
    'upload.subtitle':
      'Drop your job description. We extract title, requirements, and salary in seconds.',
    'upload.extractionEngineLabel': 'Intelligent extraction engine',
    'upload.featuresTitle': 'Automated parameter extraction',
    'upload.feature1': 'Job title and minimum years of experience.',
    'upload.feature2': 'Vacancy summary.',
    'upload.feature3': 'Salary range, currency, and whether pay is confidential.',
    'upload.feature4': 'Hard and soft skills, location, and work mode.',
    'upload.flowRawLabel': 'JD (PDF or Word)',
    'upload.flowOutputLabel': 'Vacancy parameters',
    'upload.nextStep': 'Then review the details and publish when you are ready.',
    'upload.formatsLabel': 'Accepted formats:',
    'upload.clickInstruction': 'Click on the sphere to select',
    'upload.dropZoneHint': 'Drop the file on the sphere on the right, not on the format labels.',
    'upload.formatBadgePdf': 'PDF format',
    'upload.formatBadgeDocx': 'Word format',
    'upload.dropHere': 'Drag your job description here (PDF or Word).',
    'upload.dropZoneLead': 'Drop the document directly in the interactive zone',
    'upload.fileSizeLimit': 'Limit: {{mb}} MB',
    'upload.browseFiles': 'Browse on your computer',
    'upload.privacyNote': 'Your job descriptions are processed privately and encrypted.',
    'upload.aiFeatureTitle': 'Instant vacancy extraction',
    'upload.aiFeatureBody':
      'We detect role title, responsibilities, requirements, salary range, and more to speed up publishing.',
    'upload.unsupportedFormat': 'Invalid format. Use a PDF or DOCX file.',
    'upload.uploading': 'Uploading and analyzing "{{name}}". This may take a few seconds...',
    'upload.analysisComplete': 'AI analysis complete! Loading editor...',
    'upload.stepUploading': 'Uploading file...',
    'upload.stepExtracting': 'Extracting text...',
    'upload.stepAnalyzing': 'Analyzing with AI...',
    'upload.stepExtractInfo': 'Extracting vacancy information',
    'upload.stepAnalyzingIA': 'Analyzing with AI',
    'upload.stepSaving': 'Saving results',
    'upload.timeoutMessage':
      'The process is taking longer than expected. Check the panel to see if the vacancy was created.',
    'upload.analyzingDocument': 'Analyzing document',
    'upload.analyzingDocumentMessage': 'We are analyzing the document to extract the vacancy data.',
    'upload.analyzingShort': 'One moment...',
    'upload.extractionComplete': 'Processing successful!',
    'upload.extractionCompleteMessage':
      'We have finished processing the document correctly. Review the extracted data before publishing.',
    'upload.duplicateModalTitle': 'Update existing vacancy?',
    'upload.duplicateModalMessage':
      'You already have a vacancy with the same title: "{{title}}". Do you want to update that vacancy with this document or keep the new one we just created?',
    'upload.duplicateOverwrite': 'Update that vacancy',
    'upload.duplicateKeepNew': 'Keep the new one',
    'upload.errorAnalysis': 'Error in AI analysis.',
    'upload.timeoutLong':
      'The process is taking longer than expected. Please check the vacancies panel to see if the vacancy was created correctly.',
    'upload.vacancyLimitReached':
      'You have reached the maximum number of vacancies for your plan. Set a vacancy to Closed status or upgrade your plan to upload another.',
    'upload.gcsUnavailable':
      'Could not save the file to storage. Check GCS configuration on the server (credentials and bucket name).',
    'common.failed': 'Failed',
    'common.unexpectedError': 'Unexpected error',
    'common.errorUpdate': 'Error updating',
    // ──── Premium / Checkout ────
    'premium.title': 'Premium Subscription',
    'premium.subtitle': 'Power up your recruitment processes with advanced AI.',
    'premium.period': '/ month',
    'premium.feature1': 'Unlimited Resume Analysis',
    'premium.feature2': 'AI-assisted Interviews',
    'premium.feature3': 'Detailed Candidate Reports',
    'premium.feature4': 'Priority Support',
    'premium.checkoutTitle': 'Complete Purchase',
    'premium.cardInstructions': 'Enter your card details',
    'premium.payNow': 'Pay Now',
    'premium.paying': 'Processing...',
    'premium.paid': 'Paid!',
    'premium.success': 'Thank you very much for your purchase!',
    'premium.errorInit': 'Could not initialize payment. Check your connection.',
    'premium.errorPayment': 'Payment error: {{message}}',
    'premium.errorConfirm': 'Could not confirm payment with the server.',
    // ──── Layout / Sidebar ────
    'layout.expandMenu': 'Expand menu',
    'layout.collapseMenu': 'Collapse menu',
    'layout.themeDefault': 'Classic Blue',
    'layout.themeViolet': 'Violet',
    'layout.themeBlack': 'Pure Black',
    'layout.themeLightPure': 'Pure White',
    'layout.themeVioletInverted': 'Violet Inverted',
    'layout.notifications': 'Notifications',
    'layout.notificationsHeader': 'Notifications',
    'layout.markAllRead': 'Mark all as read',
    'layout.noNotifications': 'No notifications',
    // ──── Settings – Notifications tab ────
    'settings.notifications.description':
      'Notification summary. When a new application is submitted to one of your vacancies, it will appear here with details.',
    'settings.notifications.markAllRead': 'Mark all as read',
    'settings.notifications.none': 'No notifications.',
    'settings.notifications.paginationShowing': 'Showing {from} - {to} of {total} notifications',
    'settings.notifications.vacancyLabel': 'Vacancy: ',
    'settings.notifications.candidateLabel': 'Candidate: ',
    'settings.notifications.emailLabel': 'Email: ',
    'settings.profile.photoAlt': 'Profile photo',
    'recruiter.calendar.title': 'Google Calendar',
    'recruiter.calendar.hint':
      'Connect the Google account where you manage interviews. Required to publish vacancies and match availability with candidates.',
    'recruiter.calendar.checking': 'Checking connection…',
    'recruiter.calendar.connected': 'Calendar connected',
    'recruiter.calendar.notConnected': 'Calendar not connected',
    'recruiter.calendar.connectButton': 'Connect Google Calendar',
    'recruiter.calendar.connecting': 'Connecting…',
    'recruiter.calendar.refresh': 'Check again',
    'recruiter.calendar.blockActivate':
      'Connect Google Calendar in Settings before publishing the vacancy.',
    'recruiter.calendar.activateTooltip': 'Connect your calendar in Settings to activate',
    'recruiter.calendar.statusError': 'Could not verify calendar. Please try again.',
    'recruiter.calendar.schedulingPaused': 'Calendar connected · interviews paused',
    'recruiter.calendar.pauseScheduling': 'Pause interviews',
    'recruiter.calendar.resumeScheduling': 'Resume interviews',
    'recruiter.calendar.disconnect': 'Disconnect calendar',
    // ──── Onboarding ────
    'onboarding.step1Title': 'Welcome to AI Recruiter',
    'onboarding.step1Desc':
      'Your intelligent platform to manage the recruitment process with AI. Start by creating your first vacancy.',
    'onboarding.step2Title': '1. Upload your Job Description',
    'onboarding.step2Desc':
      'Upload a PDF or DOCX file with the job description. Our AI will automatically extract all relevant information.',
    'onboarding.step3Title': '2. Edit and Customize',
    'onboarding.step3Desc':
      'Review and adjust the data extracted by AI. You can modify skills, requirements, salary, and more job details.',
    'onboarding.step4Title': '3. Configure Questions',
    'onboarding.step4Desc':
      'Customize the AI-assisted interview questions. You can add, edit, or delete questions to suit your needs.',
    'onboarding.step5Title': '4. Publish your Vacancy',
    'onboarding.step5Desc':
      'Change the status of your vacancy to ACTIVE so candidates can find and apply. You will get a public link to share.',
    'onboarding.step6Title': '5. Review Candidates',
    'onboarding.step6Desc':
      'In the Workspace you can see all candidates who applied, their match scores, AI analysis, and compare profiles easily.',
    'onboarding.step7Title': 'Ready to Start!',
    'onboarding.step7Desc':
      'Now you know the main features. Click the button to create your first vacancy and start recruiting with AI.',
    'onboarding.skip': 'Skip',
    'onboarding.previous': 'Previous',
    'onboarding.next': 'Next',
    'onboarding.start': 'Get Started',
    // ──── Upload Circle ────
    'uploadCircle.defaultText': 'Drop your file here',
    'uploadCircle.successText': 'Sent!',
    // ──── Workspace ────
    'workspace.loading': 'Calculating Match Scores...',
    'workspace.pinnedProfileLabel': 'Profile: {{name}}',
    'workspace.headerCandidate': 'Candidate',
    'workspace.headerStatus': 'Status',
    'workspace.headerExp': 'Exp.',
    'workspace.headerSkills': 'Skills',
    'workspace.headerMatch': 'MATCH',
    'workspace.headerAction': 'Action',
    'workspace.years': 'years',
    'workspace.contactLabel': 'Contact:',
    'workspace.locationLabel': 'Location:',
    'workspace.educationLabel': 'Education:',
    'workspace.experienceLabel': 'Experience:',
    'workspace.statusLabel': 'Status:',
    'workspace.iaAnalysis': 'AI Analysis',
    'workspace.experience': 'Experience',
    'workspace.technicalSkills': 'Technical Skills',
    'workspace.candidateResponses': 'Candidate Responses',
    'workspace.clickToSeeAnswers': '(Click to see answers)',
    'workspace.softSkills': 'Soft Skills',
    'workspace.education': 'Education',
    'workspace.questionLabel': 'Question {{num}}:',
    'workspace.answerLabel': 'Answer:',
    'workspace.noAnswers': 'No answers available for this candidate.',
    'workspace.noBreakdown':
      'The compatibility breakdown is not available for this candidate. It may still be calculating.',
    'workspace.detailTab.summary': 'AI Summary',
    'workspace.detailTab.compatibility': 'Compatibility',
    'workspace.detailTab.cv': 'Curriculum Vitae (CV)',
    'workspace.detailTab.answers': 'AI interview answers',
    'workspace.detailTabNavAria': 'Expanded candidate sections',
    'workspace.detailNoAiSummary': 'No AI analysis is available for this candidate.',
    'workspace.detailCvLoadError': "Could not load this candidate's CV.",
    'workspace.detailCvNoVacancy': 'Select a vacancy to view the CV.',
    'workspace.detailAnswersLoading': 'Loading answers…',
    'workspace.errorCouldNotDelete': 'Could not delete',
    'workspace.errorNetwork': 'Network error',
    'workspace.errorLoadCandidates': 'Could not load candidates list.',
    'workspace.notScored': 'Not scored',
    'workspace.estado.postulado': 'Applied',
    'workspace.estado.en_revision': 'In review',
    'workspace.estado.entrevistado': 'Interviewed',
    'workspace.estado.seleccionado': 'Selected',
    'workspace.estado.descartado': 'Not selected',
    'workspace.errorEstado': 'Could not change the candidate status.',
    'workspace.closeProcess': 'Close process',
    'workspace.closeProcessHint':
      'Closes the vacancy, hires whoever you selected and turns down the rest.',
    'workspace.closing': 'Closing...',
    'workspace.closeConfirmHired':
      'You are about to hire {{name}} and turn down {{count}} candidate(s) still in process. The vacancy will be closed. Continue?',
    'workspace.closeConfirmNoHire':
      'You have not selected anyone. {{count}} candidate(s) will be turned down and the vacancy closed without hiring. Continue?',
    'workspace.closeDone': 'Process closed. {{count}} application(s) were turned down.',
    'workspace.errorClose': 'Could not close the process.',
    'workspace.notScoredHint': 'They applied, but the AI has not scored them yet.',
    'workspace.certificationsLabel': 'Certifications:',
    // ──── Metrics ────
    'metrics.loading': 'Loading metrics...',
    'metrics.errorLoading': 'Error loading metrics',
    'metrics.pageTitle': 'Selection Metrics',
    'metrics.viewAllInWorkspace': 'View all in workspace',
    'metrics.viableThreshold': 'score ≥ 70%',
    'metrics.noDataTitle': 'No data yet',
    'metrics.noDataDesc': 'Metrics will appear when candidates apply to this vacancy.',
    'metrics.totalCandidates': 'Total Candidates',
    'metrics.applicationsReceived': 'applications received',
    'metrics.avgScore': 'Avg Score',
    'metrics.avgMatch': 'avg match',
    'metrics.topScore': 'Top Score',
    'metrics.bestCandidate': 'best candidate',
    'metrics.viableCandidates': 'Viable Candidates',
    'metrics.talentQuality': 'Talent Quality',
    'metrics.candidates': 'candidates',
    'metrics.candidatesInRange': 'Candidates in range',
    'metrics.generalMatch': 'General Match',
    'metrics.weight': 'Weight',
    'metrics.candidateSource': 'Candidate Source',
    'metrics.avgScoreLabel': 'Avg Score',
    'metrics.experienceLevel': 'Experience',
    'metrics.keySkills': 'Key Skills',
    'metrics.notEnoughData': 'Not enough data',
    'metrics.topScoreMentions': 'mentions in top scores',
    'metrics.frequencyNote': 'Frequency in candidates with score > 75%',
    'metrics.educationTitle': 'Education',
    'metrics.notSpecified': 'Not specified',
    // ──── Metrics – Radar categories ────
    'metrics.radar.experience': 'Experience',
    'metrics.radar.technicalSkills': 'Tech. Skills',
    'metrics.radar.responses': 'Responses',
    'metrics.radar.softSkills': 'Soft Skills',
    'metrics.radar.education': 'Education',
    // ──── Metrics – Education labels ────
    'metrics.edu.bachelors': "Bachelor's",
    'metrics.edu.masters': "Master's",
    'metrics.edu.doctorate': 'Doctorate',
    'metrics.edu.certifications': 'Certifications',
    'metrics.edu.other': 'Other',
    // ──── Recruiter CV form ────
    'recruiter.cv.profileTitle': 'Profile: {{name}}',
    'recruiter.cv.profileTitleFallback': 'Candidate profile',
    'recruiter.cv.readOnlySubtitle':
      'View candidate information. Only the candidate can edit their profile.',
    'recruiter.cv.editSubtitleBefore': 'Review and edit the information. Use the',
    'recruiter.cv.editSubtitleAfter': 'buttons to complete sections.',
    'recruiter.cv.loadingTitle': 'Loading candidate profile...',
    'recruiter.cv.loadingSubtitle': 'Fetching information extracted from the CV.',
    'recruiter.cv.saveChanges': 'Save changes',
    'recruiter.cv.saved': '✓ Saved',
    // ──── Candidate Flow ────
    'candidate.default': 'Candidate',
    'candidate.loading': 'Loading...',
    'candidate.expandMenu': 'Expand menu',
    'candidate.collapseMenu': 'Collapse menu',
    'layout.changeLanguage': 'Change Language',
    'candidate.switchToEnglish': 'Switch to English',
    'candidate.switchToSpanish': 'Cambiar a Español',
    'candidate.logout': 'Logout',
    // Step 1 – CV Upload
    'candidate.step1.title': 'Upload your CV',
    'candidate.step1.description': 'Drag your CV file here or click to select it',
    'candidate.step1.formats': 'Accepted formats:',
    'candidate.step1.dragHere': 'Drag your CV here',
    'candidate.step1.cvProcessed': 'CV processed',
    'candidate.step1.stepsTitle': 'Process Steps',
    'candidate.step1.stepsDescription':
      'Complete the following requirements to apply for this vacancy:',
    'candidate.step1.uploadCV': 'Upload your updated CV',
    'candidate.step1.verifyInfo': 'Verify extracted information',
    'candidate.step1.recordVideo': 'Record a presentation video',
    'candidate.step1.answerQuestions': 'Answer vacancy questions',
    'candidate.step1.linkCalendar': 'Link your Google calendar',
    'candidate.step1.scheduleInterview': 'Schedule interview day and time',
    'candidate.step1.savedCvPrompt':
      'You already have a saved CV ({name}). What would you like to do?',
    'candidate.step1.useSavedCv': 'Use my saved CV',
    'candidate.step1.uploadNewForVacancy': 'Upload a new one for this vacancy',
    'candidate.step1.selectVacancyFirst': 'Select a vacancy before using your saved CV.',
    'candidate.workspace.title': 'Candidate workspace',
    'candidate.workspace.navAria': 'Candidate navigation',
    'candidate.workspace.dashboard': 'Dashboard',
    'candidate.workspace.applications': 'My applications',
    'candidate.workspace.jobs': 'Jobs',
    'candidate.workspace.documents': 'My documents',
    'candidate.workspace.settings': 'Settings',
    'candidate.workspace.loading': 'Loading…',
    'candidate.workspace.continueApplication': 'Application in progress',
    'candidate.workspace.continueBtn': 'Continue',
    'candidate.workspace.summary': 'Summary',
    'candidate.workspace.summaryStats': '{{applications}} application(s) · {{jobs}} open job(s)',
    'candidate.workspace.noApplications': 'You have no applications yet.',
    'candidate.workspace.noJobs': 'No open jobs right now.',
    'candidate.workspace.resolved': 'Process closed',
    'candidate.workspace.submitted': 'Submitted, under review',
    'candidate.workspace.closedVacancy': 'Vacancy closed',
    'candidate.workspace.minExperience': '{{years}} year(s) of experience',
    'candidate.workspace.noMinExperience': 'No minimum experience',
    'candidate.workspace.applyBtn': 'Apply',
    'candidate.workspace.paginationShowing': 'Showing {from} - {to} of {total} applications',
    'candidate.jobs.subtitle': 'Jobs you have not applied to yet.',
    'candidate.jobs.openCount': '{{count}} open',
    'candidate.jobs.searchPlaceholder': 'Search by title, summary, or skill…',
    'candidate.jobs.foundCount': '{{count}} found',
    'candidate.jobs.quickFilters': 'Quick filters',
    'candidate.jobs.filterExperience': 'Minimum experience',
    'candidate.jobs.filterSort': 'Sort',
    'candidate.jobs.filterSkills': 'Skills',
    'candidate.jobs.experienceAll': 'All',
    'candidate.jobs.experienceNone': 'No minimum',
    'candidate.jobs.experienceUpTo2': 'Up to 2 years',
    'candidate.jobs.experienceThreePlus': '3+ years',
    'candidate.jobs.sortTitleAsc': 'A → Z',
    'candidate.jobs.sortTitleDesc': 'Z → A',
    'candidate.jobs.sortExpAsc': 'Lowest experience required',
    'candidate.jobs.sortExpDesc': 'Highest experience required',
    'candidate.jobs.clearFilters': 'Clear filters',
    'candidate.jobs.clearSearch': 'Clear search',
    'candidate.jobs.noResults': 'No jobs match these criteria.',
    'candidate.jobs.goToDocuments': 'Go to my documents',
    'candidate.jobs.masterTitle': 'Available jobs',
    'candidate.jobs.skillsTitle': 'Key skills',
    'candidate.jobs.noSummary': 'No description available.',
    'candidate.jobs.tabList': 'List ({{count}})',
    'candidate.jobs.tabDetail': 'Detail',
    'candidate.jobs.paginationShowing': 'Showing {from} - {to} of {total} jobs',
    'candidate.dashboard.portalBadge': 'Candidate portal',
    'candidate.dashboard.subtitleAttention':
      'You have {{count}} application(s) in progress that need your attention.',
    'candidate.dashboard.subtitleDefault': 'Overview of your activity on the portal.',
    'candidate.dashboard.kpiTotal': 'Total applications',
    'candidate.dashboard.kpiInProgress': 'In progress',
    'candidate.dashboard.kpiCompleted': 'Completed',
    'candidate.dashboard.kpiOpenJobs': 'Open jobs',
    'candidate.dashboard.kpiSubTotal': 'Registered on your account',
    'candidate.dashboard.kpiSubInProgress': 'Drafts or active processes',
    'candidate.dashboard.kpiSubCompleted': 'Archived or closed processes',
    'candidate.dashboard.kpiSubOpenJobs': 'Available to apply',
    'candidate.dashboard.sectionInProgress': 'Applications in progress',
    'candidate.dashboard.activeBadge': '{{count}} active',
    'candidate.dashboard.continueApplication': 'Continue application',
    'candidate.dashboard.viewDetail': 'View details',
    'candidate.dashboard.viewAllApplications': 'View all applications',
    'candidate.dashboard.showingInProgress': 'Showing {{shown}} of {{total}} in progress',
    'candidate.dashboard.noActivePending': 'No pending active applications',
    'candidate.dashboard.exploreHint': 'Browse open jobs and apply to roles that fit your profile.',
    'candidate.dashboard.suggestedJobsBadge': '{{count}} suggested jobs',
    'candidate.dashboard.exploreCatalog': 'See all jobs',
    'candidate.dashboard.quickAccess': 'Quick access',
    'candidate.dashboard.quickApplicationsDesc': 'History and statuses',
    'candidate.dashboard.quickJobsDesc': 'Open jobs',
    'candidate.dashboard.quickDocumentsDesc': 'Reusable CV',
    'candidate.dashboard.cvStatusOk': 'Up to date',
    'candidate.dashboard.onboardingTitle': 'You have no applications yet',
    'candidate.dashboard.onboardingBody':
      'Browse open jobs or upload your CV to reuse it in future applications.',
    'candidate.dashboard.exploreJobs': 'Browse jobs',
    'candidate.dashboard.uploadCv': 'Upload résumé',
    'candidate.dashboard.loadError': 'Could not load your summary.',
    'candidate.dashboard.retry': 'Retry',
    'candidate.dashboard.chipCv': 'CV',
    'candidate.dashboard.chipVideo': 'Video',
    'candidate.documents.empty': 'You have not uploaded a reusable CV yet.',
    'candidate.documents.uploadBtn': 'Upload CV (PDF or DOCX)',
    'candidate.documents.uploading': 'Uploading…',
    'candidate.documents.uploadError': 'Could not upload CV.',
    'candidate.documents.deleteError': 'Could not delete CV.',
    'candidate.documents.unnamed': 'CV.pdf',
    'candidate.documents.uploadedAt': 'Uploaded: {{date}}',
    'candidate.documents.replaceBtn': 'Replace file',
    'candidate.documents.deleteBtn': 'Delete',
    'candidate.documents.deleteTitle': 'Delete saved CV',
    'candidate.documents.deleteMessage':
      'Your global CV will be removed. Submitted applications stay unchanged.',
    'candidate.documents.deleteConfirm': 'Delete',
    'candidate.documents.badgeNoCv': 'No CV saved',
    'candidate.documents.badgeHasCv': 'CV saved',
    'candidate.documents.pageSubtitle': 'Upload a CV to reuse it when you apply to job openings.',
    'candidate.documents.tabMainFile': 'Main file',
    'candidate.documents.tabHowItWorks': 'How it works',
    'candidate.documents.mainSlotLabel': 'Primary CV slot',
    'candidate.documents.formatsHint': 'Recommended formats: PDF, DOCX',
    'candidate.documents.dropHint':
      'Drag and drop your file here or click to browse on your device.',
    'candidate.documents.selectFromDevice': 'Select file from your device',
    'candidate.documents.bulletReusable': 'Automatically reused in new applications',
    'candidate.documents.bulletFormats': 'Allowed formats: PDF and DOCX',
    'candidate.documents.bulletApplications': 'Submitted applications will not be changed',
    'candidate.documents.howItWorksTitle': 'How it works',
    'candidate.documents.howItWorksLead': 'Your global CV in 3 simple steps.',
    'candidate.documents.step1Title': 'Upload your CV once here',
    'candidate.documents.step1Desc': 'We store it securely in your profile.',
    'candidate.documents.step2Title': 'Reuse it when you apply',
    'candidate.documents.step2Desc': 'Save time by autofilling your details in one click.',
    'candidate.documents.step3Title': 'Update it anytime',
    'candidate.documents.step3Desc': 'Change your CV without affecting past applications.',
    'candidate.documents.goToJobs': 'Go to available jobs',
    'candidate.settings.theme': 'Theme',
    'candidate.settings.language': 'Language',
    'candidate.settings.subtitle': 'Customize appearance and language for your portal',
    'candidate.settings.appearanceDesc':
      'Choose the visual palette for job cards, your application flow, and portal views.',
    'candidate.settings.languageDesc': 'Set the language for the candidate interface.',
    'candidate.settings.languagePlatformLabel': 'Platform language',
    'candidate.settings.languageHeaderHelper':
      'You can also change the language anytime from the globe icon in the header.',
    'candidate.settings.activeThemePreview': 'Active theme: {{name}}',
    'candidate.settings.themesAvailable': 'Available themes',
    'candidate.settings.mobileTabTheme': 'Theme & color',
    'candidate.settings.mobileTabLanguage': 'Language',
    'candidate.application.sectionInProgress': 'In progress',
    'candidate.application.sectionCompletedClosed': 'Completed / closed',
    'candidate.application.emptyInProgress': 'You have no applications in progress.',
    'candidate.application.emptyCompleted': 'You have no completed or closed applications.',
    'candidate.application.totalProcesses': '{{count}} registered applications',
    'candidate.application.activeBadge': '{{count}} active',
    'candidate.application.actionRequired': 'Action required',
    'candidate.application.viewDetail': 'View details',
    'candidate.application.chipCvDone': 'CV ✓',
    'candidate.application.chipCvPending': 'CV pending',
    'candidate.application.chipVideoDone': 'Video ✓',
    'candidate.application.chipVideoPending': 'Video pending',
    'candidate.application.exploreJobs': 'Browse open jobs',
    'candidate.application.loadError': 'We could not load your applications.',
    'candidate.application.retry': 'Retry',
    'candidate.application.backToList': 'Back to my applications',
    'candidate.application.notFound': 'We could not find this application.',
    'candidate.application.timelineTitle': 'Process status',
    'candidate.application.checklistTitle': 'Deliverables',
    'candidate.application.draftInProgressTitle': 'Draft in progress',
    'candidate.application.draftLastSaved': 'Last saved {{minutes}} min ago · Step {{step}} of 5',
    'candidate.application.resumeDraft': 'Resume where I left off',
    'candidate.application.checklistCv': 'CV',
    'candidate.application.checklistVideo': 'Video',
    'candidate.application.timeline.postulado': 'Applied',
    'candidate.application.timeline.en_revision': 'Under review',
    'candidate.application.timeline.entrevistado': 'Interviewed',
    'candidate.application.timeline.seleccionado': 'Selected',
    'candidate.application.timeline.descartado': 'Not selected',
    // Step Review Data
    'candidate.review.analyzingTitle': 'Analyzing Profile...',
    'candidate.review.analyzingSubtitle':
      'We are organizing the information extracted from your CV.',
    'candidate.review.title': 'Profile Validation',
    'candidate.review.subtitle':
      'Check that the extracted information is correct. Click on any field to edit it.',
    'candidate.review.personalInfo': 'Personal Information',
    'candidate.review.fullName': 'Full Name',
    'candidate.review.fullNamePlaceholder': 'Your name',
    'candidate.review.email': 'Professional Email',
    'candidate.review.phone': 'Contact Phone',
    'candidate.review.profileSummary': 'Profile Summary',
    'candidate.review.profileSummaryPlaceholder': 'Brief description of your career...',
    'candidate.review.vacancySource': 'How did you hear about this vacancy?',
    'candidate.review.selectOption': 'Select an option',
    'candidate.review.referred': 'Referred / Acquaintance',
    'candidate.review.jobBoard': 'Job Board (OCC, CompuTrabajo, etc.)',
    'candidate.review.companyWebsite': 'Company Website',
    'candidate.review.socialNetworks': 'Social Networks (Facebook, Instagram, X)',
    'candidate.review.university': 'University / School',
    'candidate.review.other': 'Other',
    'candidate.review.workExperience': 'Work Experience',
    'candidate.review.jobTitle': 'Job Title / Position',
    'candidate.review.company': 'Company',
    'candidate.review.period': 'Period',
    'candidate.review.start': 'Start',
    'candidate.review.end': 'End',
    'candidate.review.location': 'Location',
    'candidate.review.responsibilities': 'Responsibilities and Achievements',
    'candidate.review.responsibilitiesPlaceholder': 'One responsibility per line',
    'candidate.review.education': 'Education',
    'candidate.review.degree': 'Degree / Academic Title',
    'candidate.review.institution': 'University / School Institution',
    'candidate.review.projects': 'Highlighted Projects',
    'candidate.review.projectName': 'Project Name',
    'candidate.review.technologies': 'Technologies Used',
    'candidate.review.projectDescription': 'Project Description',
    'candidate.review.skills': 'Skills',
    'candidate.review.languages': 'Languages',
    'candidate.review.certifications': 'Certifications',
    'candidate.review.certName': 'Certification Name',
    'candidate.review.certIssuer': 'Issuing Entity',
    'candidate.review.saving': '⏳ Saving...',
    'candidate.review.confirmed': '✓ Data Confirmed!',
    'candidate.review.confirmData': 'Confirm Data',
    // Step 2 – Video Recorder
    'candidate.video.prepTitle': 'Get ready for your video presentation',
    'candidate.video.prepSubtitle':
      'Briefly explain your experience and why you are interested in this position',
    'candidate.video.lighting': 'Lighting',
    'candidate.video.lightingTip':
      'Make sure you are in a well-lit place, preferably with light facing you.',
    'candidate.video.sound': 'Sound',
    'candidate.video.soundTip': 'Find a quiet environment so we can hear you clearly.',
    'candidate.video.framing': 'Framing',
    'candidate.video.framingTip':
      'Place the camera at eye level and maintain a comfortable distance.',
    'candidate.video.naturalness': 'Naturalness',
    'candidate.video.naturalnessTip':
      "Don't worry about being perfect, be yourself. We are excited to hear from you.",
    'candidate.video.start': 'Start',
    'candidate.video.recordingTitle': 'Interview Recording',
    'candidate.video.cameraPreview': 'Camera preview',
    'candidate.video.startRecording': 'Start Recording',
    'candidate.video.stopRecording': 'Stop Recording',
    'candidate.video.uploadRecorded': 'Upload Recorded Video',
    'candidate.video.downloadVideo': 'Download Video',
    'candidate.video.uploadSuccess': 'Video uploaded successfully!',
    'candidate.video.uploading': 'Uploading video...',
    'candidate.video.or': 'or',
    'candidate.video.uploadOwn': 'Upload Your Own Video',
    'candidate.video.selectVideo': 'Select Video',
    'candidate.video.selectedFile': 'Selected file: {{name}}',
    'candidate.video.uploadFile': 'Upload File',
    // Step 3 – Questions
    'candidate.questions.title': 'AI-Assisted Interview',
    'candidate.questions.selectVacancy': 'Select the vacancy you wish to apply for:',
    'candidate.questions.loadingVacancies': 'Loading available vacancies...',
    'candidate.questions.noVacancies': 'No available vacancies found.',
    'candidate.questions.selectThis': 'Select This Vacancy',
    'candidate.questions.yearsExp': '{{years}}+ years',
    'candidate.questions.answerPlaceholder': 'Write your answer here...',
    'candidate.questions.charCount': '{{count}} / {{max}} characters',
    'candidate.questions.shortAnswerHint': 'A more detailed answer scores better.',
    'candidate.questions.preparingInterview': 'Preparing your personalized interview...',
    'candidate.questions.answerInstructions':
      'Answer the following questions to complete your interview:',
    'candidate.questions.changeVacancy': 'Change Vacancy',
    'candidate.questions.salaryLabel': 'What is your salary expectation?',
    'candidate.questions.salaryPlaceholder': 'E.g.: $50,000 - $60,000 MXN monthly',
    'candidate.questions.salaryHint': 'You can specify a range or a specific amount',
    'candidate.questions.submit': 'Submit Answers',
    'candidate.questions.incompleteAlert': 'Answer every question to continue.',
    'candidate.questions.errorSave': 'Error saving answers: {{error}}',
    'candidate.questions.noQuestions': 'Could not get questions for this vacancy.',
    'candidate.questions.pendingReview':
      'Questions for this vacancy are being reviewed by the recruiter. Please try again later.',
    'candidate.questions.rejected':
      'Questions for this vacancy were rejected. The recruiter must generate new questions.',
    'candidate.questions.checkConnection': 'Check your connection or try again.',
    'candidate.questions.tryAgain': 'Try Again',
    // Step 4 – Calendar
    'candidate.calendar.verifying': 'Verifying calendar access...',
    'candidate.calendar.processing': 'Processing Connection...',
    'candidate.calendar.processingMessage':
      'We are confirming the connection with Google. Please wait a moment, this may take a few seconds.',
    'candidate.calendar.connectTitle': 'Connect your Calendar for Interviews',
    'candidate.calendar.connectMessage':
      'Connect your Google Calendar so we can see your availability and send you interview invitations.',
    'candidate.calendar.connectButton': 'Connect Google Calendar',
    'candidate.calendar.loadingUser': 'Loading User...',
    'candidate.calendar.waitingAuth': 'Waiting for Firebase authentication to get your user ID...',
    'candidate.calendar.errorConnect': 'Error connecting to Google Calendar. Please try again.',
    'candidate.calendar.errorRetry': 'Error: {{error}}. Please try again.',
    'candidate.scheduling.prepareTitle': 'Prepare your schedule',
    'candidate.scheduling.prepareSubtitle':
      'Review your Google Calendar and the company schedule before picking a time.',
    'candidate.scheduling.row.candidate': 'Your Google Calendar',
    'candidate.scheduling.row.candidateHint':
      'We need read access to cross-check your availability.',
    'candidate.scheduling.row.company': 'Company schedule',
    'candidate.scheduling.row.companyHint':
      'The company confirms the assigned recruiter availability.',
    'candidate.scheduling.status.pending': 'Pending',
    'candidate.scheduling.status.ready': 'Ready',
    'candidate.scheduling.status.paused': 'Paused',
    'candidate.scheduling.status.unavailable': 'Unavailable',
    'candidate.scheduling.status.checking': 'Checking…',
    'candidate.scheduling.whyAccess': 'Why do we ask for access?',
    'candidate.scheduling.whyModal.title': 'Why do we ask for calendar access?',
    'candidate.scheduling.whyModal.body':
      'We use your calendar only to show free slots and send interview invites. We never publish events without your confirmation.',
    'candidate.scheduling.whyModal.privacyLink': 'View privacy notice',
    'candidate.scheduling.waitRoom.title': 'You cannot pick a time yet',
    'candidate.scheduling.waitRoom.retry': 'Retry',
    'candidate.scheduling.waitRoom.retrying': 'Retrying…',
    'candidate.scheduling.waitRoom.generic': 'We are waiting for both calendars to be ready.',
    'candidate.scheduling.waitRoom.recruiterNotConnected':
      'The recruiter has not connected their calendar yet. We will notify you when you can schedule.',
    'candidate.scheduling.waitRoom.recruiterPaused':
      'The recruiter temporarily paused interviews. Try again in a few minutes.',
    'candidate.scheduling.waitRoom.recruiterUnavailable':
      'We could not verify the recruiter calendar. Try again soon.',
    'candidate.scheduling.waitRoom.candidateNotConnected':
      'Connect your Google Calendar in the previous step to continue.',
    'candidate.scheduling.waitRoom.candidateUnavailable':
      'Reconnect your calendar; we could not read your availability.',
    // Step 5 – Calendar Success
    'candidate.calendarSuccess.title': 'Calendar Connected Successfully',
    'candidate.calendarSuccess.message':
      'Thank you for connecting your Google Calendar. We can now verify your availability for future interviews.',
    'candidate.calendarSuccess.errorTitle': 'Calendar Connection Failed',
    'candidate.calendarSuccess.errorDetail': 'Error Detail:',
    'candidate.calendarSuccess.errorHint':
      'Please try again. Ensure you grant all permissions requested by Google.',
    'candidate.calendarSuccess.defaultError': 'An unknown error occurred during connection.',
    'candidate.calendarSuccess.back': 'Back to home',
    'candidate.calendarSuccess.pickSchedule': 'Pick a time',
    'candidate.calendarSuccess.goHome': 'Go to home',
    'candidate.calendarSuccess.retryConnect': 'Retry connection',
    // Step 6 – Schedule Interview
    'candidate.schedule.title': 'Schedule Interview',
    'candidate.schedule.description': 'Select an available day and time for your interview.',
    'candidate.schedule.queryingTimes': 'Querying available times...',
    'candidate.schedule.notice': 'Notice:',
    'candidate.schedule.reconnect': 'Reconnect Google Calendar',
    'candidate.schedule.fallbackHint': 'You can choose day and time below.',
    'candidate.schedule.recruiterCalendarWarning':
      "We could not verify the recruiter's calendar; shown times may be confirmed or adjusted later.",
    'candidate.schedule.recruiterCalendarNotConnected':
      'The recruiter has not connected their calendar yet; shown times may not match their real availability.',
    'candidate.schedule.selectDay': 'Select a day:',
    'candidate.schedule.availableTimes': 'Available times:',
    'candidate.schedule.noSlots': 'No times available for this day.',
    'candidate.schedule.selected': 'You have selected: {{date}} at {{time}}',
    'candidate.schedule.confirm': 'Confirm Interview',
    'candidate.schedule.scheduling': 'Scheduling...',
    'candidate.schedule.processingTitle': 'Processing your appointment...',
    'candidate.schedule.processingMessage':
      'We are scheduling your interview and sending the corresponding invitations.',
    'candidate.schedule.completedTitle': 'Application Completed!',
    'candidate.schedule.completedMessage':
      'You have successfully completed all steps of your application.',
    'candidate.schedule.proposedTime': 'Proposed Time for Interview',
    'candidate.schedule.pendingValidation':
      "The recruiter will review your profile and, if viable, the interview will be confirmed via Google Meet. You will receive a notification by email. The appointment may be subject to changes according to the recruiter's availability.",
    'candidate.schedule.thanks': 'Thank you for applying! We wish you much success.',
    'candidate.schedule.applyOther': 'Apply to another vacancy',
    'candidate.schedule.finishProcess': 'Finish process',
    'candidate.schedule.exploreOther': 'Explore other vacancies',
    'candidate.schedule.exploreMessage':
      'You can still apply to other positions that match your profile:',
    'candidate.schedule.backConfirmation': 'Back to my confirmation',
    'candidate.schedule.authError': 'Authentication error. Please restart the session.',
    'candidate.schedule.selectTime': 'Please select a time.',
    'candidate.schedule.tokenNotFound': 'Session token not found.',
    'candidate.schedule.errorSchedule': 'Error scheduling the interview',
    'candidate.schedule.errorScheduleAlert': 'Error scheduling interview: {{error}}',
    'candidate.schedule.mustAuth': 'You must be authenticated to connect your calendar.',
    'candidate.schedule.errorQueryAvailability': 'Failed to query availability:',
    'candidate.schedule.errorFetchAvailability': 'Error fetching availability.',
    'candidate.schedule.calendarReconnectRequired':
      'Could not read your calendar. Please reconnect Google Calendar.',
    'candidate.schedule.retryFetch': 'Try again',
    'candidate.schedule.confirmBlockedRecruiterCalendar':
      'You cannot confirm until the recruiter connects their calendar.',
    'candidate.schedule.errors.recruiterCalendarNotConnected':
      'The recruiter has not connected their calendar yet. Please try again later.',
    'candidate.schedule.errors.recruiterCalendarUnavailable':
      'We could not verify the recruiter schedule. Please try again later.',
    'candidate.schedule.errors.slotUnavailable':
      'That time slot is no longer available. Please choose another.',
    'candidate.schedule.errors.candidateCalendarUnavailable':
      'Could not read your calendar. Please reconnect Google Calendar.',
    // Stepper
    'candidate.stepper.verifyingSession': 'Verifying session...',
    'candidate.stepper.loadingSession': 'Loading user session...',
    'candidate.stepper.processTitle': 'Application Process',
    'candidate.stepper.processDescription':
      'Complete each of these 5 steps to boost your professional application',
    'candidate.stepper.startProcess': 'Start Process',
    'candidate.stepper.previous': 'Previous',
    'candidate.stepper.previousStep': 'Previous step',
    'candidate.stepper.reviewLabel': 'Review',
    'candidate.stepper.interviewLabel': 'Interview',
    'candidate.stepper.scheduleLabel': 'Schedule',
    'candidate.stepper.errorAuth': 'Error: User not authenticated',
    'candidate.stepper.errorUploadCV': 'Error uploading CV: {{error}}',
    'candidate.stepper.errorExtractCV':
      'Your CV was saved, but we could not read it automatically: {{error}}. Please try again in a few minutes.',
    'candidate.stepper.errorExtractCVUnknown': 'the reading service is unavailable',
    'candidate.stepper.alreadyApplied':
      'You already applied to this vacancy on {{date}}. If you continue, you will replace your previous CV, video and answers. Continue?',
    'candidate.stepper.alreadyResolved':
      'This vacancy is already resolved and your application is "{{estado}}". No further changes can be sent.',
    'candidate.stepper.previousInProgress':
      'You already applied to this vacancy on {{date}}. Status: {{estado}}. Sending again will replace your previous submission.',
    'candidate.stepper.previousResolved':
      'This vacancy is already resolved. Your application ended as: {{estado}}.',
    'candidate.stepper.seeOtherVacancies': 'See other vacancies',
    'candidate.stepper.errorUploadVideo': 'Error uploading video: {{error}}',
    'candidate.stepper.errorConfirmData': 'Error confirming data: {{error}}',
    'candidate.stepper.processingSuccess': 'Processing Successful!',
    'candidate.stepper.processingSuccessMessage':
      'We have successfully finished processing your information.',
    'candidate.stepper.confirmingProfile': 'Confirming Profile',
    'candidate.stepper.confirmingProfileMessage':
      'Validating your professional data for the next step.',
    'candidate.stepper.sendingAnswers': 'Sending Answers',
    'candidate.stepper.sendingAnswersMessage':
      'We are processing your interview for the recruiter.',
    'candidate.stepper.processingCV': 'Processing your CV',
    'candidate.stepper.processingCVMessage':
      'We are analyzing your career to speed up your professional application.',
    'candidate.stepper.processingVideo': 'Processing your Video',
    'candidate.stepper.processingVideoMessage':
      'We are analyzing your video to evaluate your skills and competencies.',
    'candidate.stepper.cvStep1': 'Uploading CV to our servers',
    'candidate.stepper.cvStep2': 'Extracting professional information with AI',
    'candidate.stepper.cvStep3': 'Structuring candidate profile',
    'candidate.stepper.videoStep1': 'Uploading video',
    'candidate.stepper.videoStep2': 'Analyzing feelings and emotions',
    'candidate.stepper.videoStep3': 'Evaluating verbal and non-verbal communication',
    'candidate.stepper.videoStep4': 'Calculating scoring and saving results',
    'candidate.stepper.stepBadge': 'Step {{current}} of {{total}}',
    'candidate.stepper.continue': 'Continue',
    'candidate.stepper.footerUploadHint':
      'Upload your CV or use the one saved to your account to continue.',
    'candidate.stepper.footerStartHint': 'Start the process to continue.',
    'candidate.stepper.footerVideoHint': 'Record or upload your video to continue.',
    'candidate.stepper.step1AiBanner':
      'Our AI engine will analyze your CV to autocomplete your profile in the review step.',
    'candidate.stepper.step3OwnWordsTitle': 'Answer from your own experience',
    'candidate.stepper.step3OwnWordsBody':
      'The recruiting team reads these answers together with your CV and your video. Describe a concrete case, in your own words. A generic answer, or one written by an AI tool, does not show how you work and usually falls behind someone who describes something they actually did.',
    'candidate.stepper.step1TipTitle': 'Tip to stand out',
    'candidate.stepper.step1TipBody':
      'Include concrete impact metrics in recent projects (percentages, savings, reach).',
    // Saite Companion
    'candidate.saite.step1': "Hi! I'm Saity. Upload your CV to start, I'll be here to guide you!",
    'candidate.saite.step2': "Done! Let's check that your info is perfect before moving on.",
    'candidate.saite.step3': "Great profile! Now get ready, let's record a short video interview.",
    'candidate.saite.step4': 'Excellent video! Now some quick questions to get to know you better.',
    'candidate.saite.step5': 'Almost there! Connect your calendar to schedule our chat.',
    'candidate.saite.step6': 'You did it! Choose the time that works best for your interview.',
    'candidate.review.addWorkExp': '+ Add Experience',
    'candidate.review.noWorkExp':
      'No work experience was detected in your CV. You can add it manually.',
    'candidate.review.removeExperience': 'Remove this experience',
    'candidate.review.remove': 'Remove',
    'candidate.review.addEducation': '+ Add Education',
    'candidate.review.noEducation':
      'No academic background was detected in your CV. You can add it manually.',
    'candidate.review.removeEducation': 'Remove this education',
    'candidate.review.details': 'Additional details',
    'candidate.review.add': '+ Add',
    'candidate.review.noSkills': 'No skills were detected. Add them manually.',
    'candidate.review.selectCategory': 'Select a category',
    'candidate.review.skillsPlaceholder': 'Skills separated by comma',
    'candidate.review.noLanguages': 'No languages were detected. Add them manually.',
    'candidate.review.languagePlaceholder': 'Language (e.g., English)',
    'candidate.review.levelPlaceholder': 'Level (e.g., B2, Advanced)',
    'candidate.review.addProject': '+ Add Project',
    'candidate.review.noProjects':
      'No projects were detected. If you have relevant projects, add them here.',
    'candidate.review.removeProject': 'Remove this project',
    'candidate.review.technologiesPlaceholder': 'React, Python, PostgreSQL...',
    'candidate.review.technologiesHint': '(separated by comma)',
    'candidate.review.projectDescriptionPlaceholder':
      'What did the project consist of? What was your role?',
    'candidate.review.addCertification': '+ Add Certification',
    'candidate.review.noCertifications':
      'No certifications were detected. If you have relevant certificates, add them here.',
    'candidate.review.removeCertification': 'Remove this certification',
    'candidate.review.issueDate': 'Issue Date',
    'candidate.review.credentialId': 'Credential ID',
    'candidate.review.credentialPlaceholder': 'ID or verification code',
    'candidate.review.confirmSuccess': '✓ Data Confirmed!',
    'candidate.review.confirmButton': 'Confirm Data',
    'candidate.review.link': 'Link / URL',
    'candidate.review.certNamePlaceholder': 'E.g.: AWS Certified Developer',
    'candidate.review.certIssuerPlaceholder': 'E.g.: Amazon Web Services',
    'candidate.review.educationPlaceholder': 'E.g.: Systems Engineering',
    'candidate.review.institutionPlaceholder': 'Institution name',
    'candidate.review.detailsPlaceholder': 'E.g.: GPA 3.8, Graduated with Honors',
    'candidate.review.jobTitlePlaceholder': 'E.g.: Senior Developer',
    'candidate.review.companyPlaceholder': 'Company name',
    'candidate.review.locationPlaceholder': 'City, Country',
    'candidate.review.city': 'City',
    'candidate.review.state': 'State / Province',
    'candidate.review.country': 'Country',
    'candidate.review.statePlaceholder': 'State or province',
    'candidate.review.skillCategory.tecnicas': 'Technical',
    'candidate.review.skillCategory.blandas': 'Soft Skills',
    'candidate.review.skillCategory.herramientas': 'Tools',
    'candidate.review.skillCategory.programacion': 'Programming Languages',
    'candidate.review.skillCategory.frameworks': 'Frameworks & Libraries',
    'candidate.review.skillCategory.db': 'Databases',
    'candidate.review.skillCategory.devops': 'Cloud & DevOps',
    'candidate.review.skillCategory.design': 'Design',
    'candidate.review.skillCategory.management': 'Project Management',
    'candidate.review.skillCategory.others': 'Others',
    'candidate.review.source.referred': 'Referred / Acquaintance',
    'candidate.review.source.jobBoard': 'Job Board (OCC, CompuTrabajo, etc.)',
    'candidate.review.source.companyWebsite': 'Company Website',
    'candidate.review.source.socialNetworks': 'Social Networks (Facebook, Instagram, X)',
    'candidate.review.source.university': 'University / School',
    'candidate.review.source.other': 'Other',
    'candidate.video.fileSizeError':
      'The selected file weighs {{size}} MB and exceeds the limit of {{max}} MB. Please select a smaller video.',
    'candidate.video.durationHint': 'Maximum {{max}} minutes long.',
    'candidate.video.durationError':
      'The video is {{duration}} min long and exceeds the {{max}} min limit. Choose a shorter video.',
    'candidate.calendar.userNotAuth': 'User is not authenticated or is loading.',
    'candidate.calendar.tokenNotFound': 'Session token not found.',
    'candidate.questions.errorFetch': 'Could not get questions for this vacancy.',
    'candidate.questions.errorLoad': 'Error loading questions. Please try again.',
    'shared.profilePhotoAlt': 'Profile photo',
  },
}

const ThemeLanguageContext = createContext<ThemeLanguageContextValue | null>(null)

export function ThemeLanguageProvider(props: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(readTheme)
  const [lang, setLangState] = useState<LangId>(readLang)

  useEffect(() => {
    /* El estante de diseño gobierna su tema en la isla; no pisar html (portal Bren usa light/dark ahí). */
    if (document.documentElement.classList.contains('design-shelf-route')) return
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem(STORAGE_THEME, theme)
    } catch (_) {}
  }, [theme])

  useEffect(() => {
    document.documentElement.lang = lang === 'es' ? 'es' : 'en'
    try {
      localStorage.setItem(STORAGE_LANG, lang)
    } catch (_) {}
  }, [lang])

  const setTheme = useCallback((id: ThemeId) => setThemeState(id), [])
  const setLang = useCallback((id: LangId) => setLangState(id), [])

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const dict = translations[lang]
      const otherLang = lang === 'es' ? 'en' : 'es'
      let s =
        (dict && dict[key]) ?? (translations[otherLang] && translations[otherLang][key]) ?? key
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          s = s.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v))
        })
      }
      return s
    },
    [lang],
  )

  const value = useMemo<ThemeLanguageContextValue>(
    () => ({ theme, setTheme, lang, setLang, t }),
    [theme, setTheme, lang, setLang, t],
  )

  return React.createElement(ThemeLanguageContext.Provider, { value: value }, props.children)
}

export function useThemeLanguage(): ThemeLanguageContextValue {
  const ctx = useContext(ThemeLanguageContext)
  if (!ctx) {
    return {
      theme: 'default',
      setTheme: () => {},
      lang: 'es',
      setLang: () => {},
      t: (k: string, _v?: Record<string, string>) => k,
    }
  }
  return ctx
}
